import type {
  CapacityStateMessage,
  CustomLobbyErrorMessage,
  CustomLobbyStateMessage,
  FactionId,
  GameOverMessage,
  MatchAbortedMessage,
  MatchFoundMessage,
  MatchStartMessage,
  PlayerEliminatedMessage,
  PlayerLeftMessage,
  QueueStateMessage,
  ServerMessage,
  SessionResponse,
  SnapshotMessage,
  SnapshotDeltaMessage,
  TowerType,
  UnitType,
} from "@towerhold/protocol";
import { applySnapshotDelta, decodeSnapshot, FACTION_IDS } from "@towerhold/protocol";
import { CANONICAL, PLAYER_COLORS } from "./art/palettes.js";
import { hydrateIcons } from "./art/icon.js";
import { createRenderer, type Renderer } from "./renderer.js";
import * as debugOverlay from "./debugOverlay.js";
import {
  DEFAULT_DOCS_SLUG,
  findDocsPage,
  getVisibleDocsPages,
  renderDocsArticleHtml,
} from "./docs/index.js";
import {
  RESEARCH_TREE,
  RESEARCH_ICON,
  TECH_INFO,
  type ResearchNode,
} from "./research/tree.js";
import { FACTIONS } from "./research/factions.js";
import "./styles.css";

const SESSION_STORAGE_KEY = "towerhold.sessionId";
const PLAYER_NAME_STORAGE_KEY = "towerhold:playerName";
const FIRST_MATCH_TOAST_KEY = "towerhold:first-match-toast-seen";
const MAX_NAME_LENGTH = 16;

const NAME_ADJECTIVES = [
  "Brave", "Sneaky", "Quiet", "Mighty", "Swift", "Clever", "Bold", "Nimble",
  "Stoic", "Merry", "Steely", "Lucky", "Wily", "Zealous", "Fierce", "Silent",
];
const NAME_ANIMALS = [
  "Wizard", "Otter", "Falcon", "Bear", "Fox", "Raven", "Badger", "Lynx",
  "Hawk", "Stag", "Wolf", "Tiger", "Hare", "Panda", "Gecko", "Eagle",
];

function generateRandomName(): string {
  const adj = NAME_ADJECTIVES[Math.floor(Math.random() * NAME_ADJECTIVES.length)]!;
  const animal = NAME_ANIMALS[Math.floor(Math.random() * NAME_ANIMALS.length)]!;
  const n = Math.floor(Math.random() * 10000).toString().padStart(4, "0");
  return `${adj} ${animal} ${n}`.slice(0, MAX_NAME_LENGTH);
}

function loadPlayerName(): string {
  const existing = window.localStorage.getItem(PLAYER_NAME_STORAGE_KEY);
  if (existing !== null && existing.trim().length > 0) return existing.slice(0, MAX_NAME_LENGTH);
  const fresh = generateRandomName();
  window.localStorage.setItem(PLAYER_NAME_STORAGE_KEY, fresh);
  return fresh;
}

function savePlayerName(name: string): string {
  const cleaned = name.replace(/[\x00-\x1F\x7F]/g, "").trim().slice(0, MAX_NAME_LENGTH);
  const final = cleaned.length > 0 ? cleaned : generateRandomName();
  window.localStorage.setItem(PLAYER_NAME_STORAGE_KEY, final);
  return final;
}

// VITE_IDENTITY_STORAGE=local (default) → localStorage. Session survives tab
// close, browser restart, and multi-window opens — players resume the match
// they're in if they reload or come back later (within the server's grace
// window for in-match reconnects).
// VITE_IDENTITY_STORAGE=session → sessionStorage (per-tab; opt-in for local
// dev when you want each tab to be a separate identity).
const identityStorage: Storage =
  import.meta.env.VITE_IDENTITY_STORAGE === "session"
    ? window.sessionStorage
    : window.localStorage;

// ---------------------------------------------------------------------------
// Game constants mirrored on client for UI cost display
// ---------------------------------------------------------------------------

/** Build cost (Arrow only) per the new ladder. All other towers must reach via upgrade. */
const TOWER_BUILD_COSTS: Record<TowerType, number> = {
  arrow:    10,
  ballista: 30,    // upgrade-only; mirrors server cost field
  cannon:   30,    // upgrade-only
  arbalest: 200,   // upgrade-only
  harpoon:  200,   // upgrade-only
  mortar:   200,   // upgrade-only
  polybolos: 1300, // upgrade-only
  skyhook:   1300, // upgrade-only
  howitzer:  1300, // upgrade-only
  skorpion:  9000, // upgrade-only
  stormpike: 9000, // upgrade-only
  culverin:  9000, // upgrade-only
  manticore:  60000, // upgrade-only
  skybreaker: 60000, // upgrade-only
  bombard:    60000, // upgrade-only
};

/** Per-tower upgrade fee when moving INTO this tower type. */
const TOWER_UPGRADE_COST: Record<TowerType, number | null> = {
  arrow: null,
  ballista: 30,
  cannon:   30,
  arbalest: 200,
  harpoon:  200,
  mortar:   200,
  polybolos: 1300,
  skyhook:   1300,
  howitzer:  1300,
  skorpion:  9000,
  stormpike: 9000,
  culverin:  9000,
  manticore:  60000,
  skybreaker: 60000,
  bombard:    60000,
};

/** Branched upgrade map. Mirrors server TOWER_UPGRADE_TARGETS. */
const TOWER_UPGRADE_TARGETS: Record<TowerType, TowerType[]> = {
  arrow:    ["ballista", "cannon"],
  ballista: ["arbalest", "harpoon"],
  cannon:   ["mortar"],
  arbalest: ["polybolos"],
  harpoon:  ["skyhook"],
  mortar:   ["howitzer"],
  polybolos: ["skorpion"],
  skyhook:   ["stormpike"],
  howitzer:  ["culverin"],
  skorpion:  ["manticore"],
  stormpike: ["skybreaker"],
  culverin:  ["bombard"],
  manticore:  [],
  skybreaker: [],
  bombard:    [],
};

/** Tech node id required to upgrade INTO this tower type. Ballista is granted by tier_1 (default-owned). */
const TECH_FOR_UPGRADE: Record<TowerType, string | null> = {
  arrow:    null,
  ballista: "tier_1", // default-owned — always satisfied
  cannon:   "t1_b1_cannon",
  arbalest: "t2_main",
  harpoon:  "t2_b3_harpoon",
  mortar:   "t2_main",  // source-tower check (Cannon required) enforces Cannon path
  polybolos: "t3_main",
  skyhook:   "t3_main",
  howitzer:  "t3_main",
  skorpion:  "t4_main",
  stormpike: "t4_main",
  culverin:  "t4_main",
  manticore:  "t5_main",
  skybreaker: "t5_main",
  bombard:    "t5_main",
};

const SELL_REFUND_RATE = 0.7; // mirrors server

const UNIT_COSTS: Record<UnitType, number> = {
  chicken: 10,
  peasant: 25,
  militia: 60,
  elder: 150,
  footmen: 150,
  squire: 250,
  soldier: 600,
  lancer: 1300,
  skyLancer: 1650,
  juggernaut: 3000,
  raider: 3000,
  duelist: 5000,
  berserker: 10000,
  paladin: 22000,
  griffinRider: 45000,
  minotaurs: 75000,
  behemoth: 135000,
  wyvern: 250000,
  warGolem: 450000,
  shinigami: 1000000,
  dragon: 1200000,
  titan: 1400000,
  colossus: 1500000,
};

const UNIT_INFO: Record<UnitType, { desc: string; kind: "ground" | "air"; attacking: boolean; income: number; hp: number; sendCount: number; armor: number }> = {
  // Flavor copy follows the voice bible at directives/specs/UNIT_FLAVOR.md.
  // Empty desc = intentional beta placeholder; tooltip skips the line cleanly.
  chicken: { desc: "When the chicken escapes the hen, it lands on your neighbor's lane! <em>peep peep</em>", kind: "ground", attacking: false, income: 2,  hp: 30,  sendCount: 1, armor: 0 },
  peasant: { desc: "The peasant walks because the king said walk", kind: "ground", attacking: false, income: 5, hp: 80, sendCount: 1, armor: 0 },
  militia: { desc: "The militia presses forward at a half-run, knowing the chance, knowing the cost, taking it anyway", kind: "ground", attacking: false, income: 10, hp: 102, sendCount: 2, armor: 0 },
  elder:   { desc: "The elder walks with a deliberate weight, the way a man does when fear has long stopped being useful", kind: "ground", attacking: false, income: 22, hp: 657, sendCount: 1, armor: 0 },
  footmen: { desc: "The footmen hold their place because the line is the only thing that has ever worked", kind: "ground", attacking: false, income: 25, hp: 190, sendCount: 3, armor: 0 },
  squire:  { desc: "The squire marches in proper form, the form he was taught, the only form that makes him a soldier", kind: "ground", attacking: false, income: 34, hp: 939, sendCount: 1, armor: 0 },
  soldier: { desc: "The soldier reads the lane the way another man reads a room, and steps where the towers are looking elsewhere", kind: "ground", attacking: false, income: 73, hp: 1199, sendCount: 2, armor: 0 },
  lancer:  { desc: "", kind: "ground", attacking: false, income: 145, hp: 5483, sendCount: 1, armor: 0 },
  skyLancer: { desc: "", kind: "air", attacking: false, income: 145, hp: 5483, sendCount: 1, armor: 0 },
  juggernaut: { desc: "The juggernaut walks a straight line because nothing in front of him has yet been reason enough to turn", kind: "ground", attacking: false, income: 302, hp: 18780, sendCount: 1, armor: 5 },
  raider:  { desc: "", kind: "ground", attacking: true, income: 76, hp: 8448, sendCount: 1, armor: 5 },
  duelist: { desc: "The duelist enters the lane the way he enters the arena, slow, smiling, watching the towers choose which one strikes first", kind: "ground", attacking: false, income: 473, hp: 11589, sendCount: 2, armor: 0 },
  berserker: { desc: "", kind: "ground", attacking: true, income: 148, hp: 12164, sendCount: 2, armor: 0 },
  paladin:  { desc: "", kind: "ground", attacking: false, income: 1745, hp: 150768, sendCount: 1, armor: 5 },
  griffinRider: { desc: "", kind: "air", attacking: false, income: 1638, hp: 178815, sendCount: 1, armor: 0 },
  minotaurs: { desc: "", kind: "ground", attacking: false, income: 5134, hp: 140070, sendCount: 3, armor: 0 },
  behemoth: { desc: "", kind: "ground", attacking: true, income: 687, hp: 394064, sendCount: 1, armor: 0 },
  wyvern:   { desc: "", kind: "air", attacking: false, income: 7384, hp: 1066737, sendCount: 1, armor: 0 },
  warGolem: { desc: "The war golem advances with the patience of stone, and registers each tower's strike the way stone registers rain", kind: "ground", attacking: false, income: 24745, hp: 4001098, sendCount: 1, armor: 10 },
  shinigami: { desc: "", kind: "ground", attacking: false, income: 50000, hp: 5164609, sendCount: 1, armor: 0 },
  dragon: { desc: "", kind: "air", attacking: false, income: 29345, hp: 5714120, sendCount: 1, armor: 0 },
  titan:  { desc: "", kind: "ground", attacking: false, income: 67203, hp: 12830571, sendCount: 1, armor: 25 },
  colossus: { desc: "", kind: "ground", attacking: true, income: 0, hp: 7253213, sendCount: 1, armor: 5 },
};

const TOWER_INFO: Record<TowerType, { desc: string; hp: number; dps: number; range: number }> = {
  arrow:    { desc: "Entry-level tower. Build one to establish your defence — upgrade when units get heavier.",                hp: 200,  dps: 10,    range: 3 },
  ballista: { desc: "Generalist upgrade from Arrow. Higher fire rate, +50 dmg/hit. Handles mid-tier units comfortably.",       hp: 360,  dps: 40,    range: 3 },
  cannon:   { desc: "Splash specialist (T1) — 1.7-cell radius, ground only. Wins on packs of cheap units; loses on lone bulks.", hp: 360, dps: 17,    range: 3 },
  arbalest: { desc: "T2 generalist — Ballista's successor. Higher fire rate and damage. Ground + air.",                        hp: 680,  dps: 240,   range: 3 },
  harpoon:  { desc: "T2 anti-air specialist. +27% DPS premium and +1 range vs the generalist, but ground units pass untouched.", hp: 680, dps: 305,  range: 4 },
  mortar:   { desc: "T2 splash specialist — 1.7-cell radius, ground only. The Cannon line's payoff for committing to crowds.", hp: 680,  dps: 104,   range: 3 },
  polybolos: { desc: "T3 generalist — Arbalest's straight successor. Faster fire rate, much higher damage. Ground + air.",     hp: 1200, dps: 1540,  range: 3 },
  skyhook:   { desc: "T3 air specialist — Harpoon's successor. Much higher damage. Air-only, range 4.",                        hp: 1200, dps: 1956,  range: 4 },
  howitzer:  { desc: "T3 splash specialist — Mortar's successor. Same 2.5s cooldown, much higher damage. Ground only.",        hp: 1200, dps: 668,   range: 3 },
  skorpion:  { desc: "T4 generalist — Polybolos's successor. Ground + air.",                                                   hp: 2040, dps: 10540, range: 3 },
  stormpike: { desc: "T4 air specialist — Skyhook's successor. Air-only, range 4.",                                            hp: 2040, dps: 13386, range: 4 },
  culverin:  { desc: "T4 splash specialist — Howitzer's successor. Ground only.",                                              hp: 2040, dps: 4574,  range: 3 },
  manticore:  { desc: "T5 generalist — Skorpion's successor. The apex of the generalist line. Ground + air.",                  hp: 3360, dps: 70540, range: 3 },
  skybreaker: { desc: "T5 air specialist — Stormpike's successor. The apex of the AA line. Air-only, range 4.",                hp: 3360, dps: 89586, range: 4 },
  bombard:    { desc: "T5 splash specialist — Culverin's successor. The apex of the splash line. Ground only.",                hp: 3360, dps: 30614, range: 3 },
};

const INCOME_INTERVAL_TICKS = 120; // must match server
const INCOME_RING_RADIUS = 40;
const INCOME_RING_CIRC = 2 * Math.PI * INCOME_RING_RADIUS;
let prevIncomeRingOffset = 0;

// Stockpile params — must match server values in apps/server/src/domain/units.ts.
// Per-unit caps and refresh ticks per ECONOMY_BLUEPRINT § Stockpile rules.
const STOCKPILE_CAP: Record<UnitType, number> = {
  chicken: 20, peasant: 15, militia: 10, elder: 5, footmen: 5,
  squire: 15, soldier: 10, lancer: 10, skyLancer: 10, juggernaut: 5, raider: 5,
  duelist: 10, berserker: 5, paladin: 10, griffinRider: 5,
  minotaurs: 10, behemoth: 5, wyvern: 5, warGolem: 5,
  shinigami: 10, dragon: 5, titan: 5, colossus: 3,
};
// Per-unit refresh: 4s / 5s / 6s / 7s / 8s / 15s / 30s per spec precedence rules.
// 1 charge fills every N ticks (100ms/tick).
const STOCKPILE_FILL_TICKS: Record<UnitType, number> = {
  chicken: 40, peasant: 50, militia: 60, elder: 60, footmen: 60,
  squire: 60, soldier: 60, lancer: 60, skyLancer: 60, juggernaut: 60, raider: 80,
  duelist: 60, berserker: 80, paladin: 60, griffinRider: 70,
  minotaurs: 60, behemoth: 80, wyvern: 70, warGolem: 60,
  shinigami: 60, dragon: 70, titan: 150, colossus: 300,
};
// Unlock delays — uniform by u-slot: u1=120, u2=240, u3=360, u4=480 ticks (12/24/36/48s).
// Anchored to tier-research moment (T1 from match start). Fork-research units honor the
// same clock — researching past the delay window makes the unit instantly available.
const UNIT_UNLOCK_DELAY_TICKS: Record<UnitType, number> = {
  chicken: 120, peasant: 240, militia: 360, elder: 480, footmen: 480,
  squire: 120, soldier: 240, lancer: 360, skyLancer: 360, juggernaut: 480, raider: 480,
  duelist: 120, berserker: 240, paladin: 360, griffinRider: 480,
  minotaurs: 120, behemoth: 240, wyvern: 360, warGolem: 480,
  shinigami: 120, dragon: 240, titan: 360, colossus: 480,
};

const TOWER_NAMES: Record<TowerType, string> = {
  arrow:    "Arrow",
  ballista: "Ballista",
  cannon:   "Cannon",
  arbalest: "Arbalest",
  harpoon:  "Harpoon",
  mortar:   "Mortar",
  polybolos: "Polybolos",
  skyhook:   "Skyhook",
  howitzer:  "Howitzer",
  skorpion:  "Skorpion",
  stormpike: "Stormpike",
  culverin:  "Culverin",
  manticore:  "Manticore",
  skybreaker: "Skybreaker",
  bombard:    "Bombard",
};

type UnitSlot = { id: string; name: string; cost: number; real?: UnitType; gateTech?: string };

// T1 spine + u4 fork slot. The 4th slot resolves to Elder or Footmen depending
// on which fork the player researches (t1_b2_elder or t1_b2_footmen).
const TIER1: UnitSlot[] = [
  { id: "chicken", name: "Chicken", cost: UNIT_COSTS.chicken, real: "chicken", gateTech: "tier_1" },
  { id: "peasant", name: "Peasant", cost: UNIT_COSTS.peasant, real: "peasant", gateTech: "tier_1" },
  { id: "militia", name: "Militia", cost: UNIT_COSTS.militia, real: "militia", gateTech: "tier_1" },
  { id: "elder",   name: "Elder",   cost: UNIT_COSTS.elder,   real: "elder",   gateTech: "t1_b2_elder" },
];
// T2 spine + u4 fork. The 3rd slot (Lancer) reskins to Sky Lancer when
// t2_b1_airmount is researched — substitution research, the server rejects
// send_unit("lancer") post-airmount (see canSendUnit / handleSendUnit). The
// 4th slot is the juggernaut/raider fork.
const TIER2: UnitSlot[] = [
  { id: "squire",     name: "Squire",     cost: UNIT_COSTS.squire,     real: "squire",     gateTech: "t2_main" },
  { id: "soldier",    name: "Soldier",    cost: UNIT_COSTS.soldier,    real: "soldier",    gateTech: "t2_main" },
  { id: "lancer",     name: "Lancer",     cost: UNIT_COSTS.lancer,     real: "lancer",     gateTech: "t2_main" },
  { id: "juggernaut", name: "Juggernaut", cost: UNIT_COSTS.juggernaut, real: "juggernaut", gateTech: "t2_b2_juggernaut" },
];

// T3 spine — all four units gate on `t3_main`.
const TIER3: UnitSlot[] = [
  { id: "duelist",      name: "Duelist",       cost: UNIT_COSTS.duelist,      real: "duelist",      gateTech: "t3_main" },
  { id: "berserker",    name: "Berserker",     cost: UNIT_COSTS.berserker,    real: "berserker",    gateTech: "t3_main" },
  { id: "paladin",      name: "Paladin",       cost: UNIT_COSTS.paladin,      real: "paladin",      gateTech: "t3_main" },
  { id: "griffinRider", name: "Griffin Rider", cost: UNIT_COSTS.griffinRider, real: "griffinRider", gateTech: "t3_main" },
];

// T4 spine — all four units gate on `t4_main`.
const TIER4: UnitSlot[] = [
  { id: "minotaurs", name: "Minotaurs", cost: UNIT_COSTS.minotaurs, real: "minotaurs", gateTech: "t4_main" },
  { id: "behemoth",  name: "Behemoth",  cost: UNIT_COSTS.behemoth,  real: "behemoth",  gateTech: "t4_main" },
  { id: "wyvern",    name: "Wyvern",    cost: UNIT_COSTS.wyvern,    real: "wyvern",    gateTech: "t4_main" },
  { id: "warGolem",  name: "War Golem", cost: UNIT_COSTS.warGolem,  real: "warGolem",  gateTech: "t4_main" },
];

// T5 spine — all four units gate on `t5_main`. Titans.
const TIER5: UnitSlot[] = [
  { id: "shinigami", name: "Shinigami", cost: UNIT_COSTS.shinigami, real: "shinigami", gateTech: "t5_main" },
  { id: "dragon",    name: "Dragon",    cost: UNIT_COSTS.dragon,    real: "dragon",    gateTech: "t5_main" },
  { id: "titan",     name: "Titan",     cost: UNIT_COSTS.titan,     real: "titan",     gateTech: "t5_main" },
  { id: "colossus",  name: "Colossus",  cost: UNIT_COSTS.colossus,  real: "colossus",  gateTech: "t5_main" },
];

// The Unit Pad is a fixed 3-row × 4-col grid. Rows are bound to the QWER /
// ASDF / ZXCV hotkey rows; which actual tier (T1..T5) sits on each row slides
// upward as the player researches `tN_main`: the bottom row is always the
// player's current tier, the top two rows show the previous two tiers (or
// empty placeholders if the player is at T1/T2).
const UNIT_TIERS: UnitSlot[][] = [TIER1, TIER2, TIER3, TIER4, TIER5];
const FIXED_HOTKEYS: string[][] = [
  ["q", "w", "e", "r"],
  ["a", "s", "d", "f"],
  ["z", "x", "c", "v"],
];
const UNIT_PAD_ROWS = 3;

/** 1-indexed: highest researched `tN_main` (default 1). Drives the slide. */
function currentPlayerTier(): number {
  const me = latestSnapshot?.players.find((p) => p.playerId === appState.sessionId);
  const owned = me?.techOwned ?? [];
  for (let n = 5; n >= 2; n--) if (owned.includes(`t${n}_main`)) return n;
  return 1;
}

/**
 * Returns the 3 rows of unit slots to render. `tierNum[r]` is the 1-indexed
 * tier sitting on display row `r`, or 0 for an empty placeholder row. Rows are
 * always exactly UNIT_PAD_ROWS long.
 */
function visibleUnitTiers(): { rows: UnitSlot[][]; tierNum: number[] } {
  const cap = Math.min(currentPlayerTier(), appState.maxTier);
  const top = Math.max(1, cap - (UNIT_PAD_ROWS - 1));
  const rows: UnitSlot[][] = [];
  const tierNum: number[] = [];
  for (let r = 0; r < UNIT_PAD_ROWS; r++) {
    const tier = top + r;
    if (tier <= cap) {
      rows.push(UNIT_TIERS[tier - 1] ?? []);
      tierNum.push(tier);
    } else {
      rows.push([]);
      tierNum.push(0);
    }
  }
  return { rows, tierNum };
}

function visibleUnitTierHotkeys(): string[][] {
  return FIXED_HOTKEYS;
}

// RESEARCH_TREE / RESEARCH_ICON / TECH_INFO and their types live in
// research/tree.ts so the wiki tier pages can render the same cards from one
// source of truth. (Imported at the top of this file.)

/** Tier-up node id per tier (the "UNLOCK" button at bottom-right of each section). */
const TIER_SPINE_NODE_ID: Record<number, string> = {
  1: "tier_1",
  2: "t2_main",
  3: "t3_main",
  4: "t4_main",
  5: "t5_main",
};

/** Each tier has exactly 3 card slots. `null` reserves the slot for a future
 *  power — rendered as a "Coming soon" card so the section layout doesn't
 *  reflow when we add the real power later. */
const TIER_SLOTS: Record<number, Array<string | null>> = {
  1: ["t1_b1_cannon",   "t1_b2_u4",   "t3_b1_lane_expansion"],
  2: ["t2_b3_harpoon",  "t2_b2_u4",   "t2_b1_airmount"],
  3: [null,             null,         null],
  4: [null,             null,         null],
  5: [null,             null,         null],
};

const RESEARCH_ROWS: Array<1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10> = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/**
 * Player-facing tier (1..5) of a research row. Tier 1 owns rows 1–2 (starter +
 * branches), tier 2 owns rows 3–4, tier 3 rows 5–6, tier 4 rows 7–8, tier 5 row 9.
 * Used to filter the research panel by `appState.maxTier`.
 */
function researchRowTier(row: number): number {
  return Math.ceil(row / 2);
}

function isRowVisible(row: number): boolean {
  return researchRowTier(row) <= appState.maxTier;
}

// ---------------------------------------------------------------------------
// App state
// ---------------------------------------------------------------------------

type LandingView = "landing" | "custom_lobby" | "docs" | "match";

type LobbyState = {
  lobbyId: string;
  hostId: string;
  players: { id: string; name: string; isBot?: boolean }[];
  maxPlayers: number;
};

type AppState = {
  sessionId: string;
  playerName: string;
  connected: boolean;
  view: LandingView;
  queuedPlayers: number;
  /** True after this client has sent `queue_join` and hasn't left or been matched. */
  inQueue: boolean;
  countdownEndsAt: number | null;
  status: string;
  activeRoomId: string | null;
  roomPlayerIds: string[];
  roomPlayerNames: Record<string, string>;
  lobbyState: LobbyState | null;
  /** If the page loaded at /r/<id>, join this lobby once the WS is connected. */
  pendingJoinLobbyId: string | null;
  /** Latest capacity broadcast; null until the first `capacity_state` arrives. */
  capacity: { active: number; cap: number | null; atCapacity: boolean } | null;
  /** When `view === "docs"`, which page slug is showing. Null = default page. */
  docsSlug: string | null;
  /**
   * Tier ladder cap. Driven by the server's `TH_MAX_TIER` and read from
   * `/api/stats` at boot, then overwritten by `match_start.maxTier` in match.
   * Filters research panel / unit pad / hotkeys / wiki sidebar so prod can
   * ship a smaller slice than beta without leaking T3+ content.
   */
  maxTier: number;
  /**
   * Factions currently available for play. Driven by `TH_FACTION_<ID>` env
   * vars on the server, read from `/api/stats` at boot, then overwritten by
   * `match_start.enabledFactions` in match. Empty → the T3/T4/T5 multichoice
   * card shows a "Coming soon" placeholder; tier unlock and the default
   * units/towers still work. Order follows the protocol's FACTION_IDS.
   */
  enabledFactions: FactionId[];
  /** Server build version from /api/stats. Shown muted in the footer so bug reports name the build. Null until first fetch. */
  serverVersion: string | null;
  /** True between `game_over` arriving and the player leaving the match. The
   *  canvas + HUD stay mounted (frozen at last frame) and the game-over overlay
   *  layers on top; tower placement and unit-send are gated on this flag. */
  postGame: boolean;
};

const appState: AppState = {
  sessionId: "",
  playerName: "",
  connected: false,
  view: "landing",
  queuedPlayers: 0,
  inQueue: false,
  countdownEndsAt: null,
  status: "Bootstrapping session...",
  activeRoomId: null,
  roomPlayerIds: [],
  roomPlayerNames: {},
  lobbyState: null,
  pendingJoinLobbyId: null,
  capacity: null,
  docsSlug: null,
  maxTier: 5,
  enabledFactions: [],
  serverVersion: null,
  postGame: false,
};

const appElement = document.querySelector<HTMLDivElement>("#app")!;

let socket: WebSocket | null = null;
let renderer: Renderer | null = null;
let latestSnapshot: SnapshotMessage | null = null;
let matchStartTime: number | null = null;
// Tracks the tier the unit pad's tier-window was last rendered for. -1 forces
// a re-render on the first snapshot of a match (and after a fresh HUD mount).
let lastRenderedPadTier = -1;
let resizeObserver: ResizeObserver | null = null;
let researchExpanded = false;
let settingsExpanded = false;

// ---------------------------------------------------------------------------
// Generic modal (used for How-to-Play from lobby chrome)
// ---------------------------------------------------------------------------

let modalOpen = false;
let modalPrevFocus: HTMLElement | null = null;

function openAppModal(bodyHtml: string, label: string): void {
  closeAppModal();
  const root = document.createElement("div");
  root.className = "app-modal";
  root.id = "app-modal-root";
  root.innerHTML = `
    <div class="app-modal-backdrop" data-dismiss="1"></div>
    <div class="app-modal-window" role="dialog" aria-modal="true" aria-label="${escapeHtml(label)}">
      <button class="app-modal-close" id="app-modal-close" aria-label="Close">✕</button>
      <div class="app-modal-body">${bodyHtml}</div>
    </div>
  `;
  document.body.appendChild(root);
  modalOpen = true;
  modalPrevFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const closeBtn = root.querySelector<HTMLButtonElement>("#app-modal-close");
  closeBtn?.focus();
  root.querySelector<HTMLElement>(".app-modal-backdrop")?.addEventListener("click", () => closeAppModal());
  closeBtn?.addEventListener("click", () => closeAppModal());
}

function closeAppModal(): void {
  const root = document.getElementById("app-modal-root");
  if (root !== null) root.remove();
  modalOpen = false;
  if (modalPrevFocus !== null) {
    try { modalPrevFocus.focus(); } catch { /* element may be gone */ }
    modalPrevFocus = null;
  }
}

document.addEventListener("keydown", (ev) => {
  if (ev.key === "Escape" && modalOpen) {
    ev.preventDefault();
    closeAppModal();
  }
});

const prevStats = new Map<string, { lives: number; gold: number; techPoints: number }>();

// ---------------------------------------------------------------------------
// Queue / lobby screen
// ---------------------------------------------------------------------------

function escapeHtml(raw: string): string {
  return raw.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] ?? c));
}

// ---------------------------------------------------------------------------
// Shell chrome — persistent top bar + atmospheric background + bottom strip
// wraps landing / custom_lobby / docs (not match, not game_over).
// ---------------------------------------------------------------------------

const ATMO_SILHOUETTE_KEYS = ["lancer", "arbalest", "raider", "soldier", "arrow"] as const;
const ACCOUNT_AVATAR_KEYS  = ["soldier", "lancer", "footmen", "raider", "skyLancer", "militia"] as const;
const BETA_TOOLTIP =
  "Work in progress — expect rough edges. Tech tree T1–T5 + endgame ladder are live; factions arrive in a future update.";

const DISCORD_URL = "https://discord.gg/ZdS5DdaU";
const DISCORD_LOGO_SVG = `<svg class="chrome-discord-icon" viewBox="0 0 127.14 96.36" aria-hidden="true" focusable="false"><path fill="currentColor" d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/></svg>`;

function hashString(input: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

function avatarKeyForName(name: string): string {
  const idx = hashString(name || "anon") % ACCOUNT_AVATAR_KEYS.length;
  return ACCOUNT_AVATAR_KEYS[idx]!;
}

function connectionDotState(): "ok" | "warn" | "err" {
  if (appState.connected) return "ok";
  if (reconnectAttempts > 0) return "warn";
  return "err";
}

function connectionLabel(): string {
  if (appState.connected) return "Connected";
  if (reconnectAttempts > 0) return "Reconnecting…";
  return "Offline";
}

/** Returns the chrome HTML wrapping the given inner content. */
function renderShellChrome(inner: string): string {
  const avatar = avatarKeyForName(appState.playerName);
  const dot = connectionDotState();
  const label = connectionLabel();
  return `
    <div class="bg-atmo" aria-hidden="true">
      ${ATMO_SILHOUETTE_KEYS.map((k, i) => `
        <div class="atmo-silhouette atmo-silhouette--p${i} icon-slot"
             data-icon="${k}" data-icon-size="160"></div>`).join("")}
    </div>
    <div class="app-chrome">
      <header class="chrome-top">
        <div class="chrome-top-left">
          <button class="brand-link" type="button" id="brand-home" title="Back to home">
            <span class="brand-wordmark">Towerhold</span>
          </button>
          <span class="beta-pill" title="${escapeHtml(BETA_TOOLTIP)}">Beta</span>
        </div>
        <div class="chrome-top-right">
          <span class="account-avatar icon-slot" data-icon="${avatar}" data-icon-size="28" aria-hidden="true"></span>
          <input class="account-name-input" id="account-name-input"
                 type="text" maxlength="${MAX_NAME_LENGTH}"
                 value="${escapeHtml(appState.playerName)}"
                 autocomplete="off" spellcheck="false" aria-label="Your name" />
          <button class="account-signin" type="button" disabled title="Accounts are coming soon">
            Sign in <span class="account-signin-soon">Soon</span>
          </button>
        </div>
      </header>
      <div class="chrome-content">
        ${inner}
      </div>
      <footer class="chrome-bottom">
        <div class="chrome-bottom-left">
          <span class="conn-dot conn-dot--${dot}" aria-hidden="true"></span>
          <span class="conn-label">${escapeHtml(label)}</span>
          ${appState.serverVersion === null ? "" : `<span class="chrome-version" title="Server build">v${escapeHtml(appState.serverVersion)}</span>`}
        </div>
        <div class="chrome-bottom-right">
          <a class="chrome-discord" href="${DISCORD_URL}" target="_blank" rel="noopener noreferrer"
             title="Join the Towerhold Discord — find a game, report bugs, share feedback">
            ${DISCORD_LOGO_SVG}<span class="chrome-discord-label">Discord</span>
          </a>
          <button class="chrome-link" type="button" id="chrome-docs-link">Wiki</button>
        </div>
      </footer>
    </div>
  `;
}

/** Hook up chrome interactions; must be called after setting innerHTML. */
function attachChromeHandlers(): void {
  hydrateIcons();

  const input = document.querySelector<HTMLInputElement>("#account-name-input");
  const avatarSlot = document.querySelector<HTMLSpanElement>(".chrome-top-right .account-avatar");
  if (input !== null) {
    let debounceTimer: number | null = null;
    const refreshAvatar = (): void => {
      if (avatarSlot === null) return;
      avatarSlot.dataset.icon = avatarKeyForName(appState.playerName);
      hydrateIcons(avatarSlot.parentElement ?? document);
    };
    const flushPending = (): void => {
      if (debounceTimer === null) return;
      window.clearTimeout(debounceTimer);
      debounceTimer = null;
      commitNameFromInput(input);
      refreshAvatar();
    };
    input.addEventListener("input", () => {
      if (debounceTimer !== null) window.clearTimeout(debounceTimer);
      // Empty stays uncommitted mid-edit — savePlayerName would otherwise
      // generate a fresh random name. The empty→random fallback only kicks
      // in on blur, which is the "user gave up editing" signal.
      if (input.value.trim().length === 0) { debounceTimer = null; return; }
      debounceTimer = window.setTimeout(() => {
        debounceTimer = null;
        commitNameFromInput(input);
        refreshAvatar();
      }, 250);
    });
    input.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter" || ev.key === "Escape") {
        ev.preventDefault();
        flushPending();
        input.blur();
      }
    });
    input.addEventListener("blur", () => { flushPending(); });
  }

  document.querySelector<HTMLButtonElement>("#brand-home")?.addEventListener("click", () => {
    if (appState.view === "landing" || appState.activeRoomId !== null) return;
    navigateTo("landing");
  });

  document.querySelector<HTMLButtonElement>("#chrome-docs-link")?.addEventListener("click", () => {
    if (appState.activeRoomId !== null) return;
    navigateTo("docs", { docsSlug: appState.docsSlug });
  });

}

/** Top-level screen dispatcher. Call this whenever appState.view or status changes. */
function renderActiveScreen(): void {
  if (appState.activeRoomId !== null) return; // match screen is mounted by mountMatchCanvas
  if (appState.view !== "landing") detachLandingShortcuts();
  if (appState.view !== "landing") stopQueueAnimations();
  switch (appState.view) {
    case "landing":       renderLandingScreen();      break;
    case "custom_lobby":  renderCustomLobbyScreen();  break;
    case "docs":          renderDocsScreen();         break;
    case "match":         /* handled by mountMatchCanvas */ break;
  }
}

/**
 * Transition to another view. If leaving the landing page while we've joined
 * the queue, send `queue_leave` so the server doesn't keep us enqueued while
 * we're elsewhere in the app.
 */
function navigateTo(view: LandingView, opts?: { docsSlug?: string | null }): void {
  if (appState.view === "landing" && view !== "landing" && appState.inQueue) {
    socket?.send(JSON.stringify({ type: "queue_leave" }));
    appState.inQueue = false;
  }
  appState.view = view;
  if (view === "docs") {
    const slug = opts?.docsSlug ?? null;
    appState.docsSlug = slug;
    setLocationPath(slug === null ? "/wiki" : `/wiki/${slug}`);
  } else if (view === "landing") setLocationPath("/");
  renderActiveScreen();
}

let landingKeydownHandler: ((ev: KeyboardEvent) => void) | null = null;
function detachLandingShortcuts(): void {
  if (landingKeydownHandler !== null) {
    document.removeEventListener("keydown", landingKeydownHandler);
    landingKeydownHandler = null;
  }
}
function attachLandingShortcuts(): void {
  detachLandingShortcuts();
  landingKeydownHandler = (ev) => {
    if (modalOpen) return;
    if (ev.target instanceof HTMLElement) {
      const tag = ev.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
    }
    if (ev.key === "?" || (ev.key === "/" && ev.shiftKey)) {
      ev.preventDefault();
      navigateTo("docs", { docsSlug: appState.docsSlug });
    }
  };
  document.addEventListener("keydown", landingKeydownHandler);
}

function renderLandingScreen(): void {
  const atCapacity = appState.capacity?.atCapacity === true && !appState.inQueue;
  const quickTitle = atCapacity ? ' title="At capacity — waiting for a slot"' : "";
  const customTitle = atCapacity ? ' title="At capacity — waiting for a slot"' : "";
  const quickDisabled = !appState.connected || atCapacity ? " disabled" : "";
  const customDisabled = !appState.connected || atCapacity ? " disabled" : "";
  const btnLabel = appState.inQueue ? "Leave Queue" : "Join Queue";
  const btnClass = appState.inQueue ? "lobby-btn-ghost" : "lobby-btn";
  const capBanner = atCapacity
    ? `<div class="capacity-banner" role="status">
         <strong>Towerhold is full right now.</strong>
         ${appState.capacity!.cap !== null ? `${appState.capacity!.active}/${appState.capacity!.cap} players in active games. ` : ""}This page will unlock automatically as soon as a slot frees — no refresh needed.
       </div>`
    : "";
  const inner = `
    <div class="screen screen--landing">
      <section class="landing-hero">
        <h1 class="landing-tagline">Towerhold</h1>
        <p class="landing-subtag">Player vs Player Tower Defense</p>
      </section>
      ${capBanner}
      <div class="mode-grid mode-grid--landing">
        <section class="mode-card mode-card--quick mode-card--queue" id="mode-quick">
          <header class="queue-card-header">
            <span class="mode-eyebrow">Matchmaking</span>
            <span class="mode-title">Quick Play</span>
            <span class="mode-sub">Click Join to enter the open queue.</span>
          </header>
          <div class="queue-card-center">
            <div class="queue-ring-wrap" id="queue-ring-wrap">
              <svg class="queue-ring" viewBox="0 0 260 260" aria-hidden="true">
                <circle class="queue-ring-track" cx="130" cy="130" r="${QUEUE_RING_RADIUS}"/>
                <circle class="queue-ring-fill"   cx="130" cy="130" r="${QUEUE_RING_RADIUS}"
                        id="queue-ring-fill"
                        style="stroke-dasharray:${QUEUE_RING_CIRC};stroke-dashoffset:${QUEUE_RING_CIRC};"/>
              </svg>
              <div class="queue-ring-center">
                <div class="queue-ring-num" id="queue-ring-num">—</div>
                <div class="queue-ring-label" id="queue-ring-label">Waiting...</div>
              </div>
            </div>
            <div class="seat-row" id="queue-seat-row" aria-label="Queued players (${appState.queuedPlayers}/${QUEUE_MAX_SEATS})">
              ${renderSeatDots()}
            </div>
          </div>
          <div class="queue-card-actions">
            <button class="${btnClass}" id="queue-toggle"${quickDisabled}${quickTitle}>${btnLabel}</button>
          </div>
        </section>
        <button class="mode-card mode-card--custom" id="mode-custom"${customDisabled}${customTitle}>
          <span class="mode-card-body">
            <span class="mode-eyebrow">Private room</span>
            <span class="mode-title">Custom Game</span>
            <span class="mode-sub">Create a private lobby and play against friends or bots.</span>
          </span>
          <span class="mode-card-silhouette icon-slot" data-icon="lancer" data-icon-size="140" aria-hidden="true"></span>
        </button>
        <button class="mode-card mode-card--wiki" id="mode-wiki">
          <span class="mode-card-body">
            <span class="mode-eyebrow">Learn</span>
            <span class="mode-title">Wiki</span>
            <span class="mode-sub">Tiers, towers, factions, and the tech tree — the full reference.</span>
          </span>
          <span class="mode-card-silhouette icon-slot" data-icon="arbalest" data-icon-size="140" aria-hidden="true"></span>
        </button>
      </div>
    </div>
  `;
  appElement.innerHTML = renderShellChrome(inner);
  attachChromeHandlers();
  attachLandingShortcuts();
  startQueueAnimations();

  document.querySelector<HTMLButtonElement>("#queue-toggle")?.addEventListener("click", (ev) => {
    ev.stopPropagation();
    toggleQueueMembership();
  });

  document.querySelector<HTMLButtonElement>("#mode-custom")?.addEventListener("click", () => {
    if (!appState.connected) return;
    if (appState.inQueue) {
      socket?.send(JSON.stringify({ type: "queue_leave" }));
      appState.inQueue = false;
    }
    socket?.send(JSON.stringify({ type: "custom_create" }));
    appState.status = "Creating room…";
    renderActiveScreen();
  });

  document.querySelector<HTMLButtonElement>("#mode-wiki")?.addEventListener("click", () => {
    navigateTo("docs", { docsSlug: appState.docsSlug });
  });

}

function toggleQueueMembership(): void {
  if (!appState.connected) return;
  if (appState.inQueue) {
    socket?.send(JSON.stringify({ type: "queue_leave" }));
    appState.inQueue = false;
    appState.status = "Left queue";
  } else {
    socket?.send(JSON.stringify({ type: "queue_join" }));
    appState.inQueue = true;
    appState.status = "Joined queue";
  }
  updateQueueToggleButton();
}

function updateQueueToggleButton(): void {
  const btn = document.getElementById("queue-toggle") as HTMLButtonElement | null;
  if (btn === null) return;
  if (appState.inQueue) {
    btn.textContent = "Leave Queue";
    btn.classList.remove("lobby-btn");
    btn.classList.add("lobby-btn-ghost");
  } else {
    btn.textContent = "Join Queue";
    btn.classList.remove("lobby-btn-ghost");
    btn.classList.add("lobby-btn");
  }
  btn.disabled = !appState.connected;
}

function updateLandingQueueUI(): void {
  const row = document.getElementById("queue-seat-row");
  if (row !== null) {
    row.innerHTML = renderSeatDots();
    row.setAttribute("aria-label", `Queued players (${appState.queuedPlayers}/${QUEUE_MAX_SEATS})`);
  }
}

function commitNameFromInput(input: HTMLInputElement): void {
  const next = savePlayerName(input.value);
  if (next === appState.playerName) {
    input.value = next;
    return;
  }
  appState.playerName = next;
  input.value = next;
  sendHello();
}

const QUEUE_MAX_SEATS = 7;
const QUEUE_COUNTDOWN_TOTAL_MS = 20_000;
const QUEUE_RING_RADIUS = 120;
const QUEUE_RING_CIRC = 2 * Math.PI * QUEUE_RING_RADIUS;

let queueAnimHandle: number | null = null;

function stopQueueAnimations(): void {
  if (queueAnimHandle !== null) { cancelAnimationFrame(queueAnimHandle); queueAnimHandle = null; }
}

function startQueueAnimations(): void {
  stopQueueAnimations();
  const ringNum = document.getElementById("queue-ring-num");
  const ringLabel = document.getElementById("queue-ring-label");
  const ringFill = document.getElementById("queue-ring-fill") as SVGCircleElement | null;
  const ringWrap = document.getElementById("queue-ring-wrap");

  const tick = (): void => {
    const end = appState.countdownEndsAt;
    if (end === null) {
      if (ringNum !== null) ringNum.textContent = `${appState.queuedPlayers}/${QUEUE_MAX_SEATS}`;
      if (ringLabel !== null) ringLabel.textContent = "Waiting for more players";
      if (ringFill !== null) ringFill.style.strokeDashoffset = String(QUEUE_RING_CIRC);
      ringWrap?.classList.remove("queue-ring--imminent");
    } else {
      const remaining = Math.max(0, end - Date.now());
      const secs = Math.ceil(remaining / 1000);
      const progress = Math.min(1, Math.max(0, remaining / QUEUE_COUNTDOWN_TOTAL_MS));
      if (ringNum !== null) ringNum.textContent = `${secs}`;
      if (ringLabel !== null) ringLabel.textContent = secs === 1 ? "second" : "seconds";
      if (ringFill !== null) ringFill.style.strokeDashoffset = String(QUEUE_RING_CIRC * (1 - progress));
      if (ringWrap !== null) {
        if (remaining <= 5000) ringWrap.classList.add("queue-ring--imminent");
        else ringWrap.classList.remove("queue-ring--imminent");
      }
    }
    queueAnimHandle = requestAnimationFrame(tick);
  };
  queueAnimHandle = requestAnimationFrame(tick);
}

function renderSeatDots(): string {
  const seats: string[] = [];
  for (let i = 0; i < QUEUE_MAX_SEATS; i++) {
    const filled = i < appState.queuedPlayers;
    if (filled) {
      const color = PLAYER_COLORS[i % PLAYER_COLORS.length]!.color;
      seats.push(`<span class="seat-dot seat-dot--filled" style="--seat-color:${color}" aria-label="Queued"></span>`);
    } else {
      seats.push(`<span class="seat-dot" aria-label="Empty seat"></span>`);
    }
  }
  return seats.join("");
}

function renderDocsScreen(): void {
  const page = findDocsPage(appState.docsSlug, appState.maxTier);
  // Reflect the resolved slug back into state so URL + sidebar stay in sync
  // even when the path was bare `/docs`, an unknown slug, or a slug hidden
  // by the env-driven tier cap (which falls back to the default page).
  appState.docsSlug = page.slug;

  const sidebarItems = getVisibleDocsPages(appState.maxTier).map((p) => {
    const active = p.slug === page.slug ? " docs-nav-link--active" : "";
    const child = p.parent !== undefined ? " docs-nav-link--child" : "";
    return `<li><a class="docs-nav-link${active}${child}" href="/wiki/${p.slug}" data-docs-slug="${p.slug}">${escapeHtml(p.title)}</a></li>`;
  }).join("");

  const { html: articleHtml, sections } = renderDocsArticleHtml(page, appState.maxTier);
  const sectionNav = sections.length > 1
    ? `<nav class="docs-section-nav" aria-label="Sections on this page">
         ${sections.map((s) => `<a class="docs-section-chip docs-section-chip--${s.slug}" href="#${s.slug}">${escapeHtml(s.title)}</a>`).join("")}
       </nav>`
    : "";

  const inner = `
    <div class="screen screen--docs">
      <details class="docs-mobile-nav">
        <summary class="docs-mobile-nav-summary">${escapeHtml(page.title)}</summary>
        <ul class="docs-mobile-nav-list">${sidebarItems}</ul>
      </details>
      <aside class="docs-sidebar" aria-label="Wiki navigation">
        <p class="docs-sidebar-eyebrow">Wiki</p>
        <ul class="docs-nav">${sidebarItems}</ul>
      </aside>
      <article class="docs-article">${sectionNav}${articleHtml}</article>
    </div>
  `;
  appElement.innerHTML = renderShellChrome(inner);
  attachChromeHandlers();

  // Wiki tier pages embed in-game research cards (`.icon-slot`s) — paint the
  // canvas silhouettes now that the article is in the DOM.
  hydrateIcons(document.querySelector(".docs-article") ?? document);

  document.querySelectorAll<HTMLAnchorElement>("a[data-docs-slug]").forEach((a) => {
    a.addEventListener("click", (ev) => {
      ev.preventDefault();
      const slug = a.getAttribute("data-docs-slug");
      navigateTo("docs", { docsSlug: slug });
    });
  });

  // Roster card accordion: clicking a row toggles its own detail panel.
  // Cards are independent — opening one does not close its siblings.
  const article = document.querySelector(".docs-article");
  article?.addEventListener("click", (ev) => {
    const button = (ev.target as HTMLElement | null)?.closest<HTMLButtonElement>(".entry-row");
    if (button === null || button === undefined) return;
    const card = button.closest<HTMLElement>(".entry-card");
    if (card === null) return;
    const detail = card.querySelector<HTMLElement>(".entry-detail");
    if (detail === null) return;
    const willOpen = button.getAttribute("aria-expanded") !== "true";
    if (willOpen) {
      detail.removeAttribute("hidden");
      button.setAttribute("aria-expanded", "true");
    } else {
      detail.setAttribute("hidden", "");
      button.setAttribute("aria-expanded", "false");
    }
  });
}

function lobbyLinkForId(lobbyId: string): string {
  return `${window.location.origin}/r/${lobbyId}`;
}

function renderCustomLobbyScreen(): void {
  const lobby = appState.lobbyState;
  if (lobby === null) {
    // Shouldn't happen; fall back to landing.
    appState.view = "landing";
    renderActiveScreen();
    return;
  }

  const isHost = lobby.hostId === appState.sessionId;
  const canStart = isHost && lobby.players.length >= 2;
  const link = lobbyLinkForId(lobby.lobbyId);
  const canAddBot = isHost && lobby.players.length < lobby.maxPlayers;

  const slotsHtml: string[] = [];
  for (let i = 0; i < lobby.maxPlayers; i++) {
    const p = lobby.players[i];
    if (p !== undefined) {
      const isMe = p.id === appState.sessionId;
      const isTheHost = p.id === lobby.hostId;
      const color = PLAYER_COLORS[i % PLAYER_COLORS.length]!.color;
      const badges: string[] = [];
      if (isTheHost)      badges.push('<span class="roster-badge roster-badge--host" title="Host">★ Host</span>');
      if (p.isBot === true) badges.push('<span class="roster-badge roster-badge--bot" title="Bot">⚙ Bot</span>');
      if (isMe)           badges.push('<span class="roster-badge roster-badge--you">You</span>');
      const remove = isHost && p.isBot === true
        ? `<button class="roster-remove" data-bot-id="${escapeHtml(p.id)}" aria-label="Remove bot">×</button>`
        : "";
      slotsHtml.push(`
        <li class="roster-slot roster-slot--filled${isMe ? " roster-slot--me" : ""}">
          <span class="seat-chip" style="--seat-color:${color}" aria-hidden="true"></span>
          <span class="roster-name">${escapeHtml(p.name)}</span>
          <span class="roster-badges">${badges.join("")}</span>
          ${remove}
        </li>
      `);
    } else {
      slotsHtml.push(`
        <li class="roster-slot roster-slot--empty">
          <span class="seat-chip seat-chip--empty" aria-hidden="true"></span>
          <span class="roster-name roster-name--empty">Empty</span>
        </li>
      `);
    }
  }

  const startTitle = canStart ? "" : ` title="Need at least 2 players to start"`;

  const inner = `
    <div class="screen screen--lobby">
      <header class="lobby-header">
        <p class="eyebrow">Custom Game</p>
        <h2 class="lobby-title">Lobby</h2>
      </header>
      <div class="lobby-grid">
        <section class="invite-card">
          <p class="eyebrow">Invite Link</p>
          <div class="invite-link-row">
            <input id="lobby-link" class="invite-link-input" readonly value="${escapeHtml(link)}"/>
            <button class="lobby-btn invite-copy" id="copy-link" type="button">Copy</button>
          </div>
          <p class="invite-hint" id="copy-hint"></p>
        </section>
        <section class="roster-card">
          <header class="roster-header">
            <p class="eyebrow">Players</p>
            <span class="roster-count"><strong>${lobby.players.length}</strong>/${lobby.maxPlayers}</span>
          </header>
          <ul class="roster-list">${slotsHtml.join("")}</ul>
        </section>
      </div>
      <div class="lobby-action-bar">
        ${isHost
          ? `<button class="lobby-btn" id="start-match" ${canStart ? "" : "disabled"}${startTitle}>Start Game</button>`
          : '<span class="lobby-waiting">Waiting for host to start…</span>'}
        ${isHost ? `<button class="lobby-btn-ghost" id="add-bot" ${canAddBot ? "" : "disabled"}>+ Add Bot</button>` : ""}
        <button class="lobby-btn-ghost" id="leave-lobby">Leave</button>
      </div>
    </div>
  `;
  appElement.innerHTML = renderShellChrome(inner);
  attachChromeHandlers();

  document.querySelector<HTMLButtonElement>("#copy-link")?.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(link);
      const hint = document.querySelector<HTMLElement>("#copy-hint");
      if (hint !== null) hint.textContent = "Link copied!";
    } catch {
      document.querySelector<HTMLInputElement>("#lobby-link")?.select();
    }
  });
  document.querySelector<HTMLButtonElement>("#start-match")?.addEventListener("click", () => {
    socket?.send(JSON.stringify({ type: "custom_start" }));
  });
  document.querySelector<HTMLButtonElement>("#add-bot")?.addEventListener("click", () => {
    socket?.send(JSON.stringify({ type: "custom_add_bot" }));
  });
  document.querySelectorAll<HTMLButtonElement>(".roster-remove").forEach((btn) => {
    btn.addEventListener("click", () => {
      const botId = btn.dataset.botId;
      if (botId === undefined || botId === "") return;
      socket?.send(JSON.stringify({ type: "custom_remove_bot", botPlayerId: botId }));
    });
  });
  document.querySelector<HTMLButtonElement>("#leave-lobby")?.addEventListener("click", () => {
    socket?.send(JSON.stringify({ type: "custom_leave" }));
    appState.lobbyState = null;
    appState.view = "landing";
    appState.status = "Left lobby";
    setLocationPath("/");
    renderActiveScreen();
  });
}

// ---------------------------------------------------------------------------
// Match screen
// ---------------------------------------------------------------------------

function mountMatchCanvas(playerCount: number): void {
  renderer?.destroy();
  resizeObserver?.disconnect();
  prevStats.clear();
  researchExpanded = false;

  appElement.innerHTML = `
    <div class="match-root" id="match-root">

      <div class="canvas-wrap" id="canvas-wrap">
        <canvas id="game-canvas"></canvas>
      </div>

      <div class="hud-player-frames" id="player-frames">
        ${renderPlayerFramesHtml()}
      </div>

      <div class="hud-topbar" id="hud-topbar">
        <div class="topbar-cell topbar-cell--stat topbar-cell--gold">
          <span class="topbar-cell-val thud-gold" id="thud-gold">0</span>
          <span class="topbar-cell-label">Gold</span>
        </div>

        <div class="topbar-cell topbar-cell--stat topbar-cell--income">
          <span class="topbar-cell-val thud-income" id="thud-income">+0</span>
          <span class="topbar-cell-label">Income</span>
        </div>

        <div class="topbar-watch-gap" id="thud-timer-wrap" aria-label="Seconds until next income">
          <div class="topbar-watch-case">
            <svg class="income-ring" viewBox="0 0 96 96" aria-hidden="true">
              <circle class="income-ring-track" cx="48" cy="48" r="${INCOME_RING_RADIUS}"/>
              <circle class="income-ring-fill"  cx="48" cy="48" r="${INCOME_RING_RADIUS}"
                      id="income-ring-fill"
                      style="stroke-dasharray:${INCOME_RING_CIRC};stroke-dashoffset:0;"/>
            </svg>
            <span class="income-ring-num thud-timer" id="thud-timer">10</span>
          </div>
        </div>

        <div class="topbar-cell topbar-cell--stat topbar-cell--lives" id="thud-lives-wrap">
          <span class="topbar-cell-val thud-lives" id="thud-lives">--</span>
          <span class="topbar-cell-label">Lives</span>
        </div>

        <button class="topbar-cell topbar-cell--stat topbar-cell--tech" id="research-btn" aria-label="Tech (T)" title="Tech (T)">
          <span class="topbar-cell-val thud-tp" id="thud-tp">0</span>
          <span class="topbar-cell-label">Tech Points</span>
        </button>

        <button class="topbar-cell topbar-cell--btn topbar-cell--menu" id="settings-btn" aria-label="Menu (Esc)" title="Menu (Esc)">
          <span class="topbar-btn-icon" aria-hidden="true">⚙</span>
        </button>
      </div>

      <div class="hud-unit-pad" id="unit-pad">
        ${renderUnitPadHtml()}
      </div>

      <div class="research-modal" id="research-modal" hidden>
        <div class="research-backdrop" id="research-backdrop"></div>
        <div class="research-window">
          <header class="research-header">
            <span class="research-points no-points" id="research-points">
              <span class="research-points-icon" aria-hidden="true">◆</span>
              <span class="research-points-val">0</span>
              <span class="research-points-label">TP</span>
            </span>
            <h3 class="research-title">Research</h3>
            <button class="research-close" id="research-close" aria-label="Close">✕</button>
          </header>
          <div class="research-tree" id="research-tree">
            ${renderResearchTreeHtml()}
          </div>
        </div>
      </div>

      <div class="research-modal settings-modal" id="settings-modal" hidden>
        <div class="research-backdrop" id="settings-backdrop"></div>
        <div class="research-window settings-window">
          <header class="research-header">
            <h3 class="research-title">Menu</h3>
            <button class="research-close" id="settings-close" aria-label="Close">✕</button>
          </header>
          <div class="settings-body">
            <a class="settings-discord-btn" href="${DISCORD_URL}" target="_blank" rel="noopener noreferrer"
               title="Bug reports + feedback in #feedback">
              ${DISCORD_LOGO_SVG}<span>Join Discord</span>
            </a>
            <button class="settings-leave-btn" id="settings-leave-btn">Leave Match</button>
          </div>
          <footer class="settings-footer">
            <span class="settings-beta" title="${escapeHtml(BETA_TOOLTIP)}">Beta</span>
          </footer>
        </div>
      </div>

    </div>
  `;

  const myLaneIndex = appState.roomPlayerIds.indexOf(appState.sessionId);
  const canvas  = document.querySelector<HTMLCanvasElement>("#game-canvas")!;
  const wrap    = document.querySelector<HTMLDivElement>("#canvas-wrap")!;
  renderer = createRenderer(canvas, wrap, playerCount, myLaneIndex);

  resizeObserver = new ResizeObserver(() => {
    renderer?.resize();
    closeRadialMenu();
  });
  resizeObserver.observe(wrap);

  setupCanvasInteraction(canvas);
  setupKeyboardShortcuts();
  setupPanelInteraction();
  hydrateIcons(document);
}

function leaveMatch(): void {
  if (appState.activeRoomId === null) return;
  socket?.send(JSON.stringify({ type: "leave_game" }));

  renderer?.destroy();
  renderer = null;
  resizeObserver?.disconnect();
  resizeObserver = null;
  matchStartTime = null;
  latestSnapshot = null;

  appState.activeRoomId    = null;
  appState.roomPlayerIds   = [];
  appState.roomPlayerNames = {};
  appState.view            = "landing";
  appState.status          = "Connected";
  appState.postGame        = false;
  prevStats.clear();
  renderActiveScreen();
}

// ---------------------------------------------------------------------------
// Panel HTML builders
// ---------------------------------------------------------------------------

function displayNameFor(id: string, fallbackIndex: number): string {
  const name = appState.roomPlayerNames[id];
  if (name !== undefined && name.length > 0) return name;
  return `P${fallbackIndex + 1}`;
}

function renderPlayerFramesHtml(): string {
  return appState.roomPlayerIds.map((id, i) => {
    const isOwn = id === appState.sessionId;
    const color = PLAYER_COLORS[i % PLAYER_COLORS.length]!.color;
    const name = displayNameFor(id, i);
    return `
      <div class="player-frame${isOwn ? " own" : ""}" id="pframe-${i}" data-player-id="${id}">
        <div class="pframe-avatar" style="background:${color}"></div>
        <div class="pframe-body">
          <div class="pframe-header">
            <div class="pframe-name">${escapeHtml(name)}</div>
            <div class="pframe-factions" id="pf-factions-${i}">
              <span class="pframe-faction" data-tier="3"></span>
              <span class="pframe-faction" data-tier="4"></span>
              <span class="pframe-faction" data-tier="5"></span>
            </div>
          </div>
          <div class="pframe-stats">
            <div class="pframe-badges">
              ${isOwn ? `<span class="pframe-pill pframe-pill--you">YOU</span>` : ""}
              <span class="pframe-pill pframe-pill--dead" id="pf-dead-${i}" hidden>DEAD</span>
              <span class="pframe-pill pframe-pill--left" id="pf-left-${i}" hidden>LEFT</span>
            </div>
            <div class="pframe-values">
              <span class="pframe-lives" id="pf-lives-${i}">--</span>
              <span class="pframe-income" id="pf-income-${i}">+--</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }).join("");
}

function renderUnitPadHtml(): string {
  const cells: string[] = [];
  const { rows, tierNum } = visibleUnitTiers();
  const hotkeys = visibleUnitTierHotkeys();
  for (let row = 0; row < UNIT_PAD_ROWS; row++) {
    const slots = rows[row] ?? [];
    const keys  = hotkeys[row] ?? [];
    const tNum  = tierNum[row] ?? 0;
    for (let col = 0; col < 4; col++) {
      const u = slots[col];
      const key = keys[col]?.toUpperCase() ?? "";
      if (u === undefined || u.real === undefined) {
        cells.push(`
          <button class="unit-slot placeholder" disabled data-tier="${row}" data-tier-num="${tNum}" data-col="${col}">
            <kbd>${key}</kbd>
          </button>
        `);
      } else {
        cells.push(`
          <button class="unit-slot"
                  data-unit-type="${u.real}"
                  data-gate-tech="${u.gateTech ?? ""}"
                  data-tier="${row}"
                  data-tier-num="${tNum}"
                  data-col="${col}">
            <kbd>${key}</kbd>
            <span class="icon-slot" data-icon="${u.real}" data-icon-size="40"></span>
            <span class="slot-cost" title="${u.cost.toLocaleString("en-US")} gold">${fmtCompact(u.cost)}</span>
            <span class="slot-stock"></span>
            <span class="slot-delay"></span>
          </button>
        `);
      }
    }
  }
  return cells.join("");
}

function renderResearchTreeHtml(): string {
  // Two short guidance paragraphs frame the tree: an intro before T1, and an
  // intermission between T2 and T3 announcing the shift to faction-gated tiers.
  const intro = `
    <p class="rtree-note">Research bolsters towers and units on the field.</p>
  `;
  const intermission = `
    <p class="rtree-note">Further advancement demands allies. Each tier unlocks a choice of faction and a set of unique powers with it.</p>
  `;
  return [
    intro,
    renderTierSectionHtml(1),
    renderTierSectionHtml(2),
    intermission,
    renderTierSectionHtml(3),
    renderTierSectionHtml(4),
    renderTierSectionHtml(5),
  ].join("");
}

const TIER_NUMERAL: Record<number, string> = { 1: "I", 2: "II", 3: "III", 4: "IV", 5: "V" };

function renderTierSectionHtml(tier: number): string {
  const spineId = TIER_SPINE_NODE_ID[tier]!;
  const spineNode = RESEARCH_TREE.find((n) => n.id === spineId)!;
  const numeral = TIER_NUMERAL[tier]!;
  // T3 / T4 / T5 are faction tiers — UNLOCKED state shows the multichoice
  // circles, PICKED state shows the 3 faction-power cards. T1 / T2 skip the
  // UNLOCKED state — unlock flips them straight to PICKED with regular slots.
  const isFactionTier = tier >= 3;
  const cardsHtml = isFactionTier
    ? renderFactionPowerSlotsHtml(tier)
    : TIER_SLOTS[tier]!.map((s) => renderCardSlotHtml(s, tier)).join("");
  const factionAreaHtml = isFactionTier ? renderFactionMultichoiceHtml(tier) : "";
  return `
    <section class="rtier rtier--tone-${tier} is-locked" id="rtier-${tier}" data-tier="${tier}">
      <header class="rtier-header">
        <span class="rtier-numeral" aria-hidden="true">${numeral}</span>
        <div class="rtier-titles">
          <span class="rtier-label">Tier&nbsp;${tier}</span>
          <span class="rtier-faction-name" hidden></span>
        </div>
      </header>
      <div class="rtier-body">
        ${factionAreaHtml}
        <div class="rcards-row${isFactionTier ? " rcards-row--faction" : ""}" data-tier="${tier}">${cardsHtml}</div>
      </div>
      <div class="rtier-actions">${renderTierUnlockHtml(spineNode)}</div>
    </section>
  `;
}

/** UNLOCKED-state body (T3/T4/T5 only): one vertical "path" panel per enabled
 *  faction — emblem, faction name, flavor text. Whole panel is the click
 *  target. Hidden by default; revealed by the .is-unlocked state class on
 *  the tier.
 *
 *  Feature-flagged via `appState.enabledFactions` (server-driven, per-faction).
 *  When any factions are still locked, a single anonymous "Coming soon" tile
 *  trails the enabled ones (same width as a real option). Tier unlock + the
 *  default units/towers granted at this tier always work; the multichoice is
 *  the only thing the flags gate. */
function renderFactionMultichoiceHtml(tier: number): string {
  const enabled = appState.enabledFactions;
  const anyDisabled = enabled.length < FACTION_IDS.length;

  const enabledHtml = enabled.map((id) => {
    const f = FACTIONS[id];
    const palette = CANONICAL[f.paletteKey];
    const primary = palette?.primary ?? "#888";
    const dark = palette?.dark ?? "#1c1917";
    const letter = f.name.slice(0, 1).toUpperCase();
    return `<button class="rfaction-option rfaction-option--${id}"
                    data-faction-id="${id}"
                    data-tier="${tier}">
      <span class="rfaction-emblem" style="background:${primary};color:${dark}" aria-hidden="true">${escapeHtml(letter)}</span>
      <span class="rfaction-name">${escapeHtml(f.name)}</span>
      <span class="rfaction-flavor">${escapeHtml(f.tooltip)}</span>
    </button>`;
  }).join("");

  let placeholderHtml = "";
  if (anyDisabled) {
    // Copy diverges by state: zero enabled → "Coming soon" reads as a wholly
    // locked feature; some enabled → "More factions" reads as a roster teaser.
    const isEmpty = enabled.length === 0;
    const name = isEmpty ? "Coming soon" : "More factions";
    const flavor = isEmpty ? "Factions arrive in a future update." : "Coming soon.";
    placeholderHtml = `
      <div class="rfaction-option rfaction-option--coming-soon"
           aria-label="More factions coming soon">
        <span class="rfaction-emblem rfaction-emblem--coming-soon" aria-hidden="true">?</span>
        <span class="rfaction-name">${name}</span>
        <span class="rfaction-flavor">${flavor}</span>
      </div>
    `;
  }

  return `
    <div class="rfaction-area" data-tier="${tier}">
      <div class="rfaction-eyebrow">Choose Your Path</div>
      <div class="rfaction-options-row">${enabledHtml}${placeholderHtml}</div>
    </div>
  `;
}

/** PICKED-state body for T3+: 3 power slots, populated when a faction is picked.
 *  Pre-pick the .rcards-row is hidden by the tier state class; the slots sit
 *  empty until the snapshot fills them via fillFactionPowerSlots. */
function renderFactionPowerSlotsHtml(tier: number): string {
  return [0, 1, 2].map((slotIdx) => `
    <div class="rcard rcard--faction-power" data-slot-idx="${slotIdx}">
      ${cardBackHtml(tier)}
    </div>
  `).join("");
}

/** Card body: one mechanical sentence under the title. Source: TECH_INFO.desc.
 *
 * Writing rule for power-card descriptions (TECH_INFO.desc / PowerDef.description):
 *   - ONE sentence, ≤80 chars. No flavor preamble, no bullet list on the card.
 *   - Verb-first: "Unlocks…", "Adds…", "Replaces…", "Substitutes…".
 *   - Third person — no "you / your".
 *   - Mechanical only — no meta opinions ("strong into X", "use when Y", etc.).
 *
 * Choice cards bypass this helper; they render their `choices[].desc` per half.
 * Choice halves are allowed light flavor (≤40 chars so the 2-line clamp doesn't
 * truncate), but still third person, still no meta opinions.
 *
 * The longer-form grants list still rides in TECH_INFO.grants for the research
 * hover-tooltip and wiki accordion — those surfaces can be wordier than a card.
 */
function cardBodyHtml(infoKey: string): string {
  const info = TECH_INFO[infoKey];
  const desc = info?.desc ?? "";
  if (desc.length === 0) return "";
  return `<div class="rcard-body"><p class="rcard-desc">${escapeHtml(desc)}</p></div>`;
}

/** Decorative back-of-card shown when the tier is sealed. */
function cardBackHtml(tier: number): string {
  const numeral = TIER_NUMERAL[tier]!;
  return `
    <span class="rcard-back" aria-hidden="true">
      <span class="rcard-back-ring"></span>
      <span class="rcard-back-numeral">${numeral}</span>
    </span>
  `;
}

// The "researched" tick is a CSS pseudo on .rcard.done (bottom-right circle
// with a ✓), so the markup stays clean — no per-card stamp element needed.
// The +N badge for repeatable Lane Expansion is the .rnode-level element.

function renderCardSlotHtml(nodeId: string | null, tier: number): string {
  if (nodeId === null) {
    return `
      <div class="rcard rcard--coming-soon" aria-label="Future power">
        ${cardBackHtml(tier)}
        <span class="rcard-coming-label">Vault</span>
      </div>
    `;
  }
  const node = RESEARCH_TREE.find((n) => n.id === nodeId)!;
  const back = cardBackHtml(tier);
  if (node.choices !== undefined) {
    // Multichoice card: top art well holds BOTH unit silhouettes side-by-side,
    // bottom is split into two clickable halves (name + 1-line desc each).
    // Left icon visually maps to the left half, right icon to the right half.
    // Assumes exactly 2 choices — the layout doesn't accommodate 3+.
    const dualIcons = node.choices.map((c) => `
      <span class="rcard-icon icon-slot rcard-icon--dual"
            data-icon="${escapeHtml(c.icon)}"
            data-icon-size="44"
            aria-hidden="true"></span>
    `).join("");
    const halves = node.choices.map((c) => `
      <button class="rcard-half"
              data-choice-id="${escapeHtml(c.id)}"
              aria-label="Pick ${escapeHtml(c.name)}">
        <span class="rcard-half-name">${escapeHtml(c.name)}</span>
        <span class="rcard-half-desc">${escapeHtml(c.desc)}</span>
      </button>
    `).join("");
    return `
      <div class="rnode rcard rcard--choice"
           id="rnode-${node.id}"
           data-node-id="${node.id}">
        ${back}
        ${renderCardHeaderHtml(node.name)}
        <div class="rcard-art rcard-art--dual">${dualIcons}</div>
        <div class="rcard-split">${halves}</div>
      </div>
    `;
  }
  if (node.repeatable === true) {
    const repeatIcon = RESEARCH_ICON[node.id] ?? "";
    return `
      <button class="rnode rcard rcard--repeatable"
              id="rnode-${node.id}"
              data-node-id="${node.id}">
        ${back}
        ${renderCardHeaderHtml(node.name)}
        <div class="rcard-art">
          <span class="rcard-icon icon-slot" data-icon="${repeatIcon}" data-icon-size="56"></span>
        </div>
        ${cardBodyHtml(node.id)}
        <span class="rnode-level" data-level="0"></span>
      </button>
    `;
  }
  const iconKey = RESEARCH_ICON[node.id] ?? "";
  return `
    <button class="rnode rcard"
            id="rnode-${node.id}"
            data-node-id="${node.id}">
      ${back}
      ${renderCardHeaderHtml(node.name)}
      <div class="rcard-art">
        <span class="rcard-icon icon-slot" data-icon="${iconKey}" data-icon-size="56"></span>
      </div>
      ${cardBodyHtml(node.id)}
    </button>
  `;
}

/** Card top zone: centered name + gold divider underneath. (The cost is
 *  always 1 tech-point on every branch card, so we don't bother showing it.) */
function renderCardHeaderHtml(name: string): string {
  return `
    <div class="rcard-header">
      <span class="rnode-name rcard-name">${escapeHtml(name)}</span>
    </div>
  `;
}

function renderTierUnlockHtml(node: ResearchNode): string {
  const cost = node.cost === 0 ? "starter" : `${fmtThousands(node.cost)}g`;
  return `
    <button class="rnode runlock lobby-btn"
            id="rnode-${node.id}"
            data-node-id="${node.id}">
      <span class="runlock-default">
        <span class="runlock-label">Unlock</span>
        <span class="runlock-cost">${cost}</span>
      </span>
      <span class="runlock-done">
        <span class="runlock-check" aria-hidden="true">✓</span>
        <span class="runlock-doneword">Unlocked</span>
      </span>
    </button>
  `;
}

// ---------------------------------------------------------------------------
// Canvas interaction
// ---------------------------------------------------------------------------

const DRAG_THRESHOLD_PX = 4;
const PAN_KEY_STEP_PX = 40;
const ZOOM_KEY_FACTOR = 1.15;

type LeftState =
  | { kind: "pending"; startX: number; startY: number }
  | { kind: "dragging"; startX: number; startY: number; curX: number; curY: number }
  | null;
type RightState = { kind: "pending" | "panning"; startX: number; startY: number } | null;

let marqueeEl: HTMLDivElement | null = null;

function showMarquee(x0: number, y0: number, x1: number, y1: number): void {
  if (marqueeEl === null) {
    marqueeEl = document.createElement("div");
    marqueeEl.className = "marquee";
    document.body.appendChild(marqueeEl);
  }
  const left = Math.min(x0, x1);
  const top  = Math.min(y0, y1);
  marqueeEl.style.left   = left + "px";
  marqueeEl.style.top    = top  + "px";
  marqueeEl.style.width  = Math.abs(x1 - x0) + "px";
  marqueeEl.style.height = Math.abs(y1 - y0) + "px";
}

function removeMarquee(): void {
  marqueeEl?.remove();
  marqueeEl = null;
}

function setupCanvasInteraction(canvas: HTMLCanvasElement): void {
  let leftState: LeftState = null;
  let rightState: RightState = null;

  canvas.addEventListener("mousedown", (e) => {
    if (e.button === 0) {
      leftState = { kind: "pending", startX: e.clientX, startY: e.clientY };
    } else if (e.button === 2) {
      rightState = { kind: "pending", startX: e.clientX, startY: e.clientY };
    }
  });

  canvas.addEventListener("mousemove", (e) => {
    if (leftState !== null) {
      if (leftState.kind === "pending") {
        const dx = e.clientX - leftState.startX;
        const dy = e.clientY - leftState.startY;
        if (Math.abs(dx) + Math.abs(dy) > DRAG_THRESHOLD_PX) {
          leftState = {
            kind: "dragging",
            startX: leftState.startX,
            startY: leftState.startY,
            curX: e.clientX,
            curY: e.clientY,
          };
          // Drag invalidates any open radial/selection.
          closeRadialMenu();
          showMarquee(leftState.startX, leftState.startY, leftState.curX, leftState.curY);
        }
      } else {
        leftState.curX = e.clientX;
        leftState.curY = e.clientY;
        showMarquee(leftState.startX, leftState.startY, leftState.curX, leftState.curY);
      }
    }
    if (rightState !== null) {
      const dx = e.clientX - rightState.startX;
      const dy = e.clientY - rightState.startY;
      if (rightState.kind === "pending" && Math.abs(dx) + Math.abs(dy) > DRAG_THRESHOLD_PX) {
        rightState.kind = "panning";
        renderer?.beginPan(rightState.startX, rightState.startY);
        closeRadialMenu();
      }
      if (rightState.kind === "panning") {
        renderer?.updatePan(e.clientX, e.clientY);
      }
    }
    renderer?.setHoverCell(renderer.cellFromClient(e.clientX, e.clientY) ?? null);
  });

  canvas.addEventListener("mouseup", (e) => {
    if (e.button === 0 && leftState !== null) {
      const wasDragging = leftState.kind === "dragging";
      const startX = leftState.startX;
      const startY = leftState.startY;
      leftState = null;

      if (wasDragging) {
        removeMarquee();
        finishMarqueeSelection(startX, startY, e.clientX, e.clientY);
        return;
      }

      if (appState.postGame) return; // Frozen battlefield — no tower placement after game-over.
      const cell = renderer?.cellFromClient(e.clientX, e.clientY) ?? null;
      if (cell === null) return;
      const myLane = appState.roomPlayerIds.indexOf(appState.sessionId);
      if (cell.laneIndex !== myLane) return;

      const me = latestSnapshot?.players.find((p) => p.playerId === appState.sessionId);
      if (me === undefined) return;
      const tower = me.towers.find((t) => t.cell.col === cell.col && t.cell.row === cell.row) ?? null;
      openRadialMenu(cell, tower, me.techOwned, me.gold);
      return;
    }

    if (e.button === 2 && rightState !== null) {
      const wasPanning = rightState.kind === "panning";
      rightState = null;
      if (wasPanning) renderer?.endPan();
    }
  });

  canvas.addEventListener("mouseleave", () => {
    if (rightState?.kind === "panning") renderer?.endPan();
    if (leftState?.kind === "dragging") removeMarquee();
    leftState = null;
    rightState = null;
    renderer?.setHoverCell(null);
  });

  canvas.addEventListener("contextmenu", (e) => {
    e.preventDefault();
  });

  canvas.addEventListener("wheel", (e) => {
    e.preventDefault();
    closeRadialMenu();
    const factor = Math.pow(1.0015, -e.deltaY);
    renderer?.zoomAroundClient(e.clientX, e.clientY, factor);
  }, { passive: false });
}

// ---------------------------------------------------------------------------
// Panel interaction — tower carousel, unit row, research, settings
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Unit tooltip
// ---------------------------------------------------------------------------

function getTooltipEl(): HTMLDivElement {
  let el = document.getElementById("slot-tooltip") as HTMLDivElement | null;
  if (!el) {
    el = document.createElement("div");
    el.id = "slot-tooltip";
    document.body.appendChild(el);
  }
  // Each tooltip caller opts in to compact via `add("tt-compact")` after this.
  el.classList.remove("tt-compact");
  return el;
}

function showUnitTooltip(btn: HTMLButtonElement): void {
  if (btn.classList.contains("locked") || btn.classList.contains("placeholder")) return;
  const sourceType = btn.dataset.unitType as UnitType | undefined;
  if (!sourceType) return;
  // Resolve the effective unit type for the tooltip. dataset.unitType stays as the
  // source unit even after substitution research (Lancer→SkyLancer, Assassin→Wraith,
  // Priest→High Priest, Colossus→Worldbreaker Colossus, Titan→Primordial Titan,
  // Dragon→Elder Dragon) because that's what the server expects on send. The icon's
  // data-icon, however, gets flipped to the effective unit by reskinForkSlots — read
  // it as the source of truth for what the tooltip should describe.
  const iconKey = btn.querySelector<HTMLElement>(".icon-slot")?.dataset.icon as UnitType | undefined;
  const unitType = iconKey ?? sourceType;
  const info = UNIT_INFO[unitType];
  const cost = UNIT_COSTS[unitType];
  const col  = parseInt(btn.dataset.col ?? "0", 10);
  const tier = parseInt(btn.dataset.tier ?? "0", 10);
  const key  = FIXED_HOTKEYS[tier]?.[col]?.toUpperCase() ?? "";
  // Prefer the canonical palette name (covers upgraded forms like "Sky Lancer",
  // "Wraith", "High Priest", "Elder Dragon" that aren't in the TIER unit-pad slot
  // arrays). Falls back to the slot name, then to the raw key.
  const name = CANONICAL[unitType]?.name
    ?? UNIT_TIERS.flat().find(s => s.real === unitType)?.name
    ?? unitType;

  const tags = [
    `<span class="tt-tag${info.kind === "air" ? " tt-tag-air" : ""}">${info.kind === "air" ? "Air" : "Ground"}</span>`,
    info.attacking ? `<span class="tt-tag tt-tag-atk">Attacking</span>` : "",
  ].join("");

  const el = getTooltipEl();
  el.innerHTML = `
    <div class="tt-header">
      <span class="tt-name">${name}</span>
      <kbd class="tt-key">${key}</kbd>
    </div>
    <div class="tt-tags">${tags}</div>
    ${info.desc ? `<p class="tt-desc">${info.desc}</p>` : ""}
    <div class="tt-stats">
      <span class="tt-cost">${cost}g</span>
      <span class="tt-income">+${info.income}g income</span>
    </div>
  `;

  const rect = btn.getBoundingClientRect();
  el.style.left = "-9999px";
  el.style.top  = "-9999px";
  el.classList.add("visible");
  requestAnimationFrame(() => {
    const h = el.offsetHeight;
    const w = el.offsetWidth;
    let top  = rect.top - h - 8;
    let left = rect.left + (rect.width / 2) - (w / 2);
    if (top < 8) top = rect.bottom + 8;
    if (left < 8) left = 8;
    if (left + w > window.innerWidth - 8) left = window.innerWidth - w - 8;
    el.style.top  = top  + "px";
    el.style.left = left + "px";
  });
}

function showTowerBuildTooltip(btn: HTMLButtonElement, type: TowerType): void {
  const el = getTooltipEl();
  el.classList.add("tt-compact");
  el.innerHTML = `
    <div class="tt-header">
      <span class="tt-name">Build ${TOWER_NAMES[type]}</span>
    </div>
  `;

  const rect = btn.getBoundingClientRect();
  el.style.left = "-9999px";
  el.style.top  = "-9999px";
  el.classList.add("visible");
  requestAnimationFrame(() => {
    const h = el.offsetHeight;
    const w = el.offsetWidth;
    let top  = rect.top - h - 8;
    let left = rect.left + (rect.width / 2) - (w / 2);
    if (top < 8) top = rect.bottom + 8;
    if (left < 8) left = 8;
    if (left + w > window.innerWidth - 8) left = window.innerWidth - w - 8;
    el.style.top  = top  + "px";
    el.style.left = left + "px";
  });
}

function showTechTooltip(btn: HTMLButtonElement): void {
  const nodeId = btn.dataset.nodeId;
  if (!nodeId) return;
  const node = RESEARCH_TREE.find((n) => n.id === nodeId);
  if (node === undefined) return;

  const me = latestSnapshot?.players.find((p) => p.playerId === appState.sessionId);
  const techOwned = me?.techOwned ?? [];

  const el = getTooltipEl();

  {
    let infoKey = nodeId;
    let displayName = node.name;
    if (node.choices !== undefined) {
      const chosen = node.choices.find((c) => techOwned.includes(c.id));
      if (chosen !== undefined) {
        infoKey = chosen.id;
        displayName = `${node.name}: ${chosen.name}`;
      }
    }
    const info = TECH_INFO[infoKey];
    if (info === undefined) return;

    const bought = nodeIsOwned(node, techOwned);
    const prereqsMet = (node.prereqs ?? []).every((p) => techOwned.includes(p));

    const prereqNames = (node.prereqs ?? [])
      .map((id) => RESEARCH_TREE.find((n) => n.id === id)?.name ?? id);

    const statusTag = bought
      ? `<span class="tt-tag tt-tag-done">Researched</span>`
      : prereqsMet
        ? `<span class="tt-tag tt-tag-atk">Available</span>`
        : `<span class="tt-tag">Locked</span>`;

    const prereqLine = !bought && !prereqsMet && prereqNames.length > 0
      ? `<p class="tt-req">Requires: ${prereqNames.join(", ")}</p>`
      : "";

    const grantsList = info.grants.map((g) => `<li>${g}</li>`).join("");

    el.innerHTML = `
      <div class="tt-header">
        <span class="tt-name">${displayName}</span>
      </div>
      <div class="tt-tags">${statusTag}</div>
      <p class="tt-desc">${info.desc}</p>
      <ul class="tt-grants">${grantsList}</ul>
      ${prereqLine}
      <div class="tt-stats">
        <span class="tt-cost">${node.tpCost !== undefined ? `${node.tpCost} pt${node.tpCost === 1 ? "" : "s"}` : node.cost === 0 ? "Free" : `${node.cost}g`}</span>
      </div>
    `;
  }

  const rect = btn.getBoundingClientRect();
  el.style.left = "-9999px";
  el.style.top  = "-9999px";
  el.classList.add("visible");
  requestAnimationFrame(() => {
    const h = el.offsetHeight;
    const w = el.offsetWidth;
    let top  = rect.top - h - 8;
    let left = rect.left + (rect.width / 2) - (w / 2);
    if (top < 8) top = rect.bottom + 8;
    if (left < 8) left = 8;
    if (left + w > window.innerWidth - 8) left = window.innerWidth - w - 8;
    el.style.top  = top  + "px";
    el.style.left = left + "px";
  });
}

function hideUnitTooltip(): void {
  const el = document.getElementById("slot-tooltip");
  el?.classList.remove("visible");
  el?.classList.remove("tt-compact");
}


// ---------------------------------------------------------------------------
// Radial cell menu — click own cell: build options on empty, sell/upgrade on existing tower
// ---------------------------------------------------------------------------

const RADIAL_BUTTON_RADIUS_PX = 52;

type RadialTower = { type: TowerType; cell: { col: number; row: number }; hp: number; maxHp: number; goldSpent: number; buildProgress: number; sellProgress: number };
type RadialOption =
  | { action: "sell" }
  | { action: "upgrade"; targetType: TowerType }
  | { action: "build"; towerType: TowerType };

function openRadialMenu(
  cell: { laneIndex: number; col: number; row: number },
  tower: RadialTower | null,
  techOwned: string[],
  gold: number,
): void {
  closeRadialMenu();
  if (!renderer) return;

  // Towers under construction/upgrade or already mid-sell are inert — no menu
  // opens so the player can't sell mid-build or stack actions. The visible
  // progress ring is the only feedback while waiting.
  if (tower !== null && (tower.buildProgress < 1 || tower.sellProgress > 0)) return;

  const isTowerUnlocked = (t: TowerType): boolean => {
    const gate = TECH_FOR_UPGRADE[t];
    return gate === null || techOwned.includes(gate);
  };

  const opts: RadialOption[] = [];
  if (tower !== null) {
    // Sell is placed first so the layout below anchors it at 6 o'clock;
    // upgrades fan out clockwise from there around the rest of the circle.
    opts.push({ action: "sell" });
    for (const targetType of TOWER_UPGRADE_TARGETS[tower.type]) {
      if (isTowerUnlocked(targetType)) {
        opts.push({ action: "upgrade", targetType });
      }
    }
  } else {
    // Arrow is the only direct-build. All other towers are reached via the
    // upgrade chain — no skipping tiers.
    opts.push({ action: "build", towerType: "arrow" });
  }

  const anchor = renderer.clientFromCell(cell.laneIndex, cell.col, cell.row);
  const menu = document.createElement("div");
  menu.id = "radial-menu";
  menu.style.left = anchor.x + "px";
  menu.style.top  = anchor.y + "px";

  const hasTower = tower !== null;
  // Tower radials anchor sell at 6 o'clock; upgrades fan out symmetrically
  // around 12. The build (no-tower) menu keeps the legacy top-start layout.
  // A lone option (sell-only on a max-tier tower, build-only on empty cell)
  // collapses to dead-center — no radius, no orbit.
  const startAngle = hasTower ? Math.PI / 2 : -Math.PI / 2;
  const step = (Math.PI * 2) / opts.length;
  const singleCenter = opts.length === 1;
  opts.forEach((opt, i) => {
    const btn = document.createElement("button");
    const variantClass = opt.action === "build" ? `radial-btn--build radial-btn--build-${opt.towerType}` : `radial-btn--${opt.action}`;
    btn.className = `radial-btn ${variantClass}`;
    btn.dataset.action = opt.action;
    if (singleCenter) {
      btn.style.transform = `translate(-50%, -50%)`;
    } else {
      const angle = startAngle + i * step;
      btn.style.transform = `translate(-50%, -50%) rotate(${angle}rad) translate(${RADIAL_BUTTON_RADIUS_PX}px) rotate(${-angle}rad)`;
    }

    if (opt.action === "build") {
      const cost = TOWER_BUILD_COSTS[opt.towerType];
      if (gold < cost) btn.classList.add("unaffordable");
      btn.innerHTML = `
        <span class="icon-slot" data-icon="${opt.towerType}" data-icon-size="28"></span>
        <span class="radial-btn-cost">${cost}g</span>
      `;
    } else if (opt.action === "sell" && tower !== null) {
      const refund = Math.floor(tower.goldSpent * SELL_REFUND_RATE);
      btn.innerHTML = `<span class="radial-btn-cost">+${refund}g</span>`;
    } else if (opt.action === "upgrade" && tower !== null) {
      const cost = TOWER_UPGRADE_COST[opt.targetType] ?? 0;
      if (gold < cost) btn.classList.add("unaffordable");
      btn.innerHTML = `
        <span class="icon-slot" data-icon="${opt.targetType}" data-icon-size="28"></span>
        <span class="radial-btn-cost">${cost}g</span>
      `;
    }

    btn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      if (opt.action === "build") {
        socket?.send(JSON.stringify({ type: "build", cell: { col: cell.col, row: cell.row }, towerType: opt.towerType }));
      } else if (opt.action === "sell") {
        socket?.send(JSON.stringify({ type: "sell_tower", cell: { col: cell.col, row: cell.row } }));
      } else {
        socket?.send(JSON.stringify({ type: "upgrade_tower", cell: { col: cell.col, row: cell.row }, targetType: opt.targetType }));
      }
      closeRadialMenu();
    });
    btn.addEventListener("mouseenter", () => {
      if (opt.action === "build") showTowerBuildTooltip(btn, opt.towerType);
      else if (opt.action === "upgrade" && tower !== null) showRadialUpgradeTooltip(btn, tower, opt.targetType);
      else if (tower !== null) showRadialSellTooltip(btn, tower);
    });
    btn.addEventListener("mouseleave", hideUnitTooltip);

    menu.appendChild(btn);
  });

  document.body.appendChild(menu);
  hydrateIcons(menu);
  requestAnimationFrame(() => menu.classList.add("visible"));

  // Defer outside-click wiring by one tick so the current contextmenu/mousedown
  // sequence doesn't immediately trip the listener.
  setTimeout(() => {
    document.addEventListener("mousedown", onRadialOutside, { capture: true });
  }, 0);
}

function closeRadialMenu(): void {
  const el = document.getElementById("radial-menu");
  if (el !== null) el.remove();
  document.removeEventListener("mousedown", onRadialOutside, { capture: true } as EventListenerOptions);
  hideUnitTooltip();
  // Batch radials populate the selection highlight; clear it on any close.
  // Single-tower radials never set selection, so this is a no-op for them.
  renderer?.setSelectedCells([]);
}

function finishMarqueeSelection(x0: number, y0: number, x1: number, y1: number): void {
  if (!renderer) return;
  const myLane = appState.roomPlayerIds.indexOf(appState.sessionId);
  if (myLane < 0) return;
  const me = latestSnapshot?.players.find((p) => p.playerId === appState.sessionId);
  if (me === undefined) return;

  const left   = Math.min(x0, x1);
  const right  = Math.max(x0, x1);
  const top    = Math.min(y0, y1);
  const bottom = Math.max(y0, y1);

  const inside: typeof me.towers = [];
  for (const t of me.towers) {
    if (t.buildProgress < 1) continue;
    if (t.sellProgress > 0) continue;
    const p = renderer.clientFromCell(myLane, t.cell.col, t.cell.row);
    if (p.x >= left && p.x <= right && p.y >= top && p.y <= bottom) inside.push(t);
  }
  if (inside.length === 0) return;

  if (inside.length === 1) {
    const t = inside[0]!;
    openRadialMenu(
      { laneIndex: myLane, col: t.cell.col, row: t.cell.row },
      t,
      me.techOwned,
      me.gold,
    );
    return;
  }

  // Group by tower type, pick the largest bucket. Tiebreak: higher tier (greater
  // upgrade-or-build cost) wins; final tiebreak by type-name for determinism.
  const buckets = new Map<TowerType, typeof inside>();
  for (const t of inside) {
    const arr = buckets.get(t.type) ?? [];
    arr.push(t);
    buckets.set(t.type, arr);
  }
  const tierKey = (t: TowerType): number =>
    (TOWER_UPGRADE_COST[t] ?? TOWER_BUILD_COSTS[t] ?? 0);
  let bestType: TowerType | null = null;
  let bestArr: typeof inside = [];
  for (const [type, arr] of buckets) {
    if (
      bestType === null ||
      arr.length > bestArr.length ||
      (arr.length === bestArr.length && tierKey(type) > tierKey(bestType)) ||
      (arr.length === bestArr.length && tierKey(type) === tierKey(bestType) && type < bestType)
    ) {
      bestType = type;
      bestArr = arr;
    }
  }
  if (bestType === null || bestArr.length < 2) return;

  renderer.setSelectedCells(
    bestArr.map((t) => ({ laneIndex: myLane, col: t.cell.col, row: t.cell.row })),
  );
  openBatchRadialMenu(
    bestType,
    bestArr.map((t) => ({ cell: { col: t.cell.col, row: t.cell.row }, goldSpent: t.goldSpent })),
    { x: x1, y: y1 },
    me.techOwned,
    me.gold,
  );
}

type BatchTower = { cell: { col: number; row: number }; goldSpent: number };

function openBatchRadialMenu(
  bucketType: TowerType,
  towers: BatchTower[],
  anchor: { x: number; y: number },
  techOwned: string[],
  gold: number,
): void {
  closeRadialMenu();
  if (!renderer || towers.length < 2) return;

  const isTowerUnlocked = (t: TowerType): boolean => {
    const gate = TECH_FOR_UPGRADE[t];
    return gate === null || techOwned.includes(gate);
  };

  type BatchOpt =
    | { kind: "upgrade"; targetType: TowerType; cost: number; affordable: number }
    | { kind: "sell"; totalRefund: number; totalSpent: number };

  // Sell is always offered for batch — it's the only option for finished-tier
  // walls (e.g. Howitzer/Manticore/etc.) and is also useful as a bulk teardown.
  // Placed first so the layout below anchors it at 6 o'clock.
  let totalSpent = 0;
  for (const t of towers) totalSpent += t.goldSpent;
  const totalRefund = Math.floor(totalSpent * SELL_REFUND_RATE);
  const opts: BatchOpt[] = [{ kind: "sell", totalRefund, totalSpent }];
  for (const targetType of TOWER_UPGRADE_TARGETS[bucketType]) {
    if (!isTowerUnlocked(targetType)) continue;
    const cost = TOWER_UPGRADE_COST[targetType] ?? 0;
    const affordable = cost > 0 ? Math.min(towers.length, Math.floor(gold / cost)) : 0;
    opts.push({ kind: "upgrade", targetType, cost, affordable });
  }

  // Re-set the selection highlight (closeRadialMenu above cleared it).
  const myLane = appState.roomPlayerIds.indexOf(appState.sessionId);
  if (myLane >= 0) {
    renderer.setSelectedCells(
      towers.map((t) => ({ laneIndex: myLane, col: t.cell.col, row: t.cell.row })),
    );
  }

  const n = towers.length;
  const menu = document.createElement("div");
  menu.id = "radial-menu";
  menu.dataset.mode = "batch";
  menu.style.left = anchor.x + "px";
  menu.style.top  = anchor.y + "px";

  // Sell anchored at 6 o'clock (opts[0]); any upgrades fan out clockwise.
  // A lone option (max-tier batch with only sell available) collapses to center.
  const startAngle = Math.PI / 2;
  const step = (Math.PI * 2) / opts.length;
  const singleCenter = opts.length === 1;
  opts.forEach((opt, i) => {
    const btn = document.createElement("button");
    if (opt.kind === "upgrade") {
      btn.className = "radial-btn radial-btn--upgrade";
      btn.dataset.action = "upgrade";
    } else {
      btn.className = "radial-btn radial-btn--sell";
      btn.dataset.action = "sell";
    }
    if (singleCenter) {
      btn.style.transform = `translate(-50%, -50%)`;
    } else {
      const angle = startAngle + i * step;
      btn.style.transform = `translate(-50%, -50%) rotate(${angle}rad) translate(${RADIAL_BUTTON_RADIUS_PX}px) rotate(${-angle}rad)`;
    }

    if (opt.kind === "upgrade") {
      if (opt.affordable === 0) btn.classList.add("unaffordable");
      const shown = opt.affordable === 0 ? n : opt.affordable;
      const ofN = (opt.affordable > 0 && opt.affordable < n) ? `<span class="radial-btn-of">of ${n}</span>` : "";
      btn.innerHTML = `
        <span class="icon-slot" data-icon="${opt.targetType}" data-icon-size="28"></span>
        <span class="radial-btn-cost">${shown}× ${opt.cost}g</span>
        ${ofN}
      `;
    } else {
      btn.innerHTML = `
        <span class="radial-btn-cost">${n}× +${opt.totalRefund}g</span>
      `;
    }

    btn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      if (opt.kind === "upgrade") {
        if (opt.affordable === 0) return;
        for (let k = 0; k < opt.affordable; k++) {
          const c = towers[k]!.cell;
          socket?.send(JSON.stringify({
            type: "upgrade_tower",
            cell: { col: c.col, row: c.row },
            targetType: opt.targetType,
          }));
        }
      } else {
        for (const t of towers) {
          socket?.send(JSON.stringify({
            type: "sell_tower",
            cell: { col: t.cell.col, row: t.cell.row },
          }));
        }
      }
      closeRadialMenu();
    });
    btn.addEventListener("mouseenter", () => {
      if (opt.kind === "upgrade") {
        // Tooltip reads only `tower.type` for the source-stat half — synthesize one.
        showRadialUpgradeTooltip(
          btn,
          { type: bucketType, cell: { col: 0, row: 0 }, hp: 0, maxHp: 0, goldSpent: 0, buildProgress: 1, sellProgress: 0 },
          opt.targetType,
        );
      } else {
        showBatchSellTooltip(btn, bucketType, n, opt.totalRefund, opt.totalSpent);
      }
    });
    btn.addEventListener("mouseleave", hideUnitTooltip);

    menu.appendChild(btn);
  });

  document.body.appendChild(menu);
  hydrateIcons(menu);
  requestAnimationFrame(() => menu.classList.add("visible"));

  setTimeout(() => {
    document.addEventListener("mousedown", onRadialOutside, { capture: true });
  }, 0);
}

function showBatchSellTooltip(
  btn: HTMLButtonElement,
  bucketType: TowerType,
  n: number,
  _totalRefund: number,
  _totalSpent: number,
): void {
  const el = getTooltipEl();
  el.classList.add("tt-compact");
  const name = TOWER_NAMES[bucketType];
  el.innerHTML = `
    <div class="tt-header"><span class="tt-name">Sell ${n}× ${name}</span></div>
  `;
  positionTooltipAbove(btn, el);
}

function onRadialOutside(ev: MouseEvent): void {
  const menu = document.getElementById("radial-menu");
  if (menu === null) return;
  if (menu.contains(ev.target as Node)) return; // click on a radial button → let it through
  // Capture-phase stop: prevent the canvas mousedown (which would arm a build) from firing.
  ev.stopPropagation();
  ev.preventDefault();
  closeRadialMenu();
}

function showRadialSellTooltip(btn: HTMLButtonElement, _tower: RadialTower): void {
  const el = getTooltipEl();
  el.classList.add("tt-compact");
  el.innerHTML = `
    <div class="tt-header"><span class="tt-name">Sell</span></div>
  `;
  positionTooltipAbove(btn, el);
}

function showRadialUpgradeTooltip(btn: HTMLButtonElement, _tower: RadialTower, targetType: TowerType): void {
  const el = getTooltipEl();
  el.classList.add("tt-compact");
  const nextName = TOWER_NAMES[targetType];
  el.innerHTML = `
    <div class="tt-header"><span class="tt-name">Upgrade to ${nextName}</span></div>
  `;
  positionTooltipAbove(btn, el);
}

function positionTooltipAbove(btn: HTMLButtonElement, el: HTMLElement): void {
  const rect = btn.getBoundingClientRect();
  el.style.left = "-9999px";
  el.style.top  = "-9999px";
  el.classList.add("visible");
  requestAnimationFrame(() => {
    const h = el.offsetHeight;
    const w = el.offsetWidth;
    let top  = rect.top - h - 8;
    let left = rect.left + rect.width / 2 - w / 2;
    if (top < 8) top = rect.bottom + 8;
    if (left < 8) left = 8;
    if (left + w > window.innerWidth - 8) left = window.innerWidth - w - 8;
    el.style.top  = top  + "px";
    el.style.left = left + "px";
  });
}

function attachUnitPadListeners(): void {
  document.querySelectorAll<HTMLButtonElement>("#unit-pad .unit-slot").forEach((btn) => {
    btn.addEventListener("click", () => {
      const type = btn.dataset.unitType as UnitType;
      if (type) sendUnit(type);
    });
    btn.addEventListener("mouseenter", () => showUnitTooltip(btn));
    btn.addEventListener("mouseleave", hideUnitTooltip);
  });
}

function setupPanelInteraction(): void {
  attachUnitPadListeners();

  document.querySelectorAll<HTMLElement>(".rnode[data-node-id]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const nodeId = btn.dataset.nodeId;
      if (!nodeId) return;
      const node = RESEARCH_TREE.find((n) => n.id === nodeId);
      if (node === undefined) return;
      // Choice cards delegate their clicks to per-half .rcard-half buttons
      // wired below — the outer div should never dispatch.
      if (node.choices !== undefined) return;
      socket?.send(JSON.stringify({ type: "buy_research", nodeId }));
    });
  });

  // Multichoice halves inside choice cards (Call to Arms, The Pit). Each
  // half-button is its own click target — clicking commits the pick directly.
  document.querySelectorAll<HTMLButtonElement>(".rcard-half[data-choice-id]").forEach((half) => {
    half.addEventListener("click", (ev) => {
      ev.stopPropagation();
      const choiceId = half.dataset.choiceId;
      if (choiceId === undefined || choiceId.length === 0) return;
      socket?.send(JSON.stringify({ type: "buy_research", nodeId: choiceId }));
    });
  });

  // Faction-pick option buttons (T3/T4/T5 multichoice). Click sends pick_faction
  // with the tier + faction id; server validates + writes state, snapshot
  // round-trips back, applyTierState flips the tier from UNLOCKED to PICKED
  // (faction-tinted bg, 3 power cards in place of the circles).
  document.querySelectorAll<HTMLButtonElement>(".rfaction-option[data-faction-id]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const factionId = btn.dataset.factionId as FactionId | undefined;
      const tierStr = btn.dataset.tier;
      if (factionId === undefined || tierStr === undefined) return;
      const tier = Number(tierStr);
      if (tier !== 3 && tier !== 4 && tier !== 5) return;
      socket?.send(JSON.stringify({ type: "pick_faction", tier, factionId }));
    });
  });

  document.querySelector<HTMLButtonElement>("#research-btn")?.addEventListener("click", toggleResearchModal);
  document.querySelector<HTMLButtonElement>("#research-close")?.addEventListener("click", () => setResearchOpen(false));
  document.querySelector<HTMLElement>("#research-backdrop")?.addEventListener("click", () => setResearchOpen(false));

  document.querySelector<HTMLButtonElement>("#settings-btn")?.addEventListener("click", () => setSettingsOpen(!settingsExpanded));
  document.querySelector<HTMLButtonElement>("#settings-close")?.addEventListener("click", () => setSettingsOpen(false));
  document.querySelector<HTMLElement>("#settings-backdrop")?.addEventListener("click", () => setSettingsOpen(false));
  document.querySelector<HTMLButtonElement>("#settings-leave-btn")?.addEventListener("click", leaveMatch);
}

function setSettingsOpen(open: boolean): void {
  settingsExpanded = open;
  const modal = document.querySelector<HTMLElement>("#settings-modal");
  if (modal === null) return;
  modal.hidden = !open;
  document.querySelector<HTMLButtonElement>("#settings-btn")?.classList.toggle("active", open);
}

function setResearchOpen(open: boolean): void {
  researchExpanded = open;
  const modal = document.querySelector<HTMLElement>("#research-modal");
  if (modal === null) return;
  modal.hidden = !open;
  document.querySelector<HTMLButtonElement>("#research-btn")?.classList.toggle("active", open);
  if (!open) {
    hideUnitTooltip();
  }
}

function toggleResearchModal(): void {
  setResearchOpen(!researchExpanded);
}

// ---------------------------------------------------------------------------
// Keyboard shortcuts
// ---------------------------------------------------------------------------

let keyboardListenerActive = false;

const CHEAT_BUFFER_SIZE = 16;
const CHEAT_CODES = ["idkfa", "summonself"] as const;
let cheatBuffer = "";
let summonModeActive = false;

function setSummonModeActive(active: boolean): void {
  summonModeActive = active;
  document.body.classList.toggle("summon-mode", active);
  const id = "summon-mode-banner";
  const existing = document.getElementById(id);
  if (active) {
    if (existing === null) {
      const el = document.createElement("div");
      el.id = id;
      el.textContent = "🎯 SUMMON MODE — clicks spawn units against YOURSELF. Type 'summonself' to exit.";
      el.style.cssText = "position:fixed;top:8px;left:50%;transform:translateX(-50%);z-index:99999;background:#b91c1c;color:#fff;padding:8px 16px;border-radius:6px;font:700 13px/1.2 system-ui,sans-serif;letter-spacing:0.4px;box-shadow:0 4px 16px rgba(0,0,0,0.5);pointer-events:none;border:2px solid #fff;";
      document.body.appendChild(el);
    }
    // Immediately enable all pads so clicks don't get swallowed before the next snapshot.
    document.querySelectorAll<HTMLButtonElement>("#unit-pad .unit-slot").forEach((btn) => {
      if (btn.dataset.unitType !== undefined) btn.disabled = false;
    });
  } else {
    existing?.remove();
  }
}

function setupKeyboardShortcuts(): void {
  if (keyboardListenerActive) return;
  keyboardListenerActive = true;

  window.addEventListener("keydown", (e) => {
    if (appState.activeRoomId === null) return;
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (e.target instanceof HTMLElement && e.target.isContentEditable) return;

    const k = e.key.toLowerCase();

    if (k.length === 1 && k >= "a" && k <= "z") {
      cheatBuffer = (cheatBuffer + k).slice(-CHEAT_BUFFER_SIZE);
      const hit = CHEAT_CODES.find((c) => cheatBuffer.endsWith(c));
      if (hit !== undefined) {
        cheatBuffer = "";
        if (hit === "summonself") {
          setSummonModeActive(!summonModeActive);
          showToast(summonModeActive ? "🎯 summon mode ON" : "🎯 summon mode OFF");
        } else {
          socket?.send(JSON.stringify({ type: "cheat", code: hit }));
          showToast(`💰 ${hit}`);
        }
        return;
      }
    }

    if (e.key === "Escape") {
      if (document.getElementById("radial-menu")) {
        closeRadialMenu();
      } else if (researchExpanded) {
        setResearchOpen(false);
      } else {
        setSettingsOpen(!settingsExpanded);
      }
      return;
    }
    if (k === "t") {
      toggleResearchModal();
      return;
    }

    // Beta-only debug overlay: dashed circles around in-flight splash shells
    // for radius tuning. Shift+R so plain `r` stays free for the T1 fork-pick
    // unit hotkey. Production silently ignores the key.
    if (k === "r" && e.shiftKey && (location.hostname.startsWith("beta.") || location.hostname === "localhost")) {
      renderer?.toggleSplashRadii?.();
      return;
    }

    // Camera: zoom & pan
    if (k === "+" || k === "=") {
      renderer?.zoomAroundCenter(ZOOM_KEY_FACTOR);
      return;
    }
    if (k === "-" || k === "_") {
      renderer?.zoomAroundCenter(1 / ZOOM_KEY_FACTOR);
      return;
    }
    if (e.key === "ArrowUp")    { e.preventDefault(); renderer?.panByClientPixels(0, -PAN_KEY_STEP_PX); return; }
    if (e.key === "ArrowDown")  { e.preventDefault(); renderer?.panByClientPixels(0,  PAN_KEY_STEP_PX); return; }
    if (e.key === "ArrowLeft")  { e.preventDefault(); renderer?.panByClientPixels(-PAN_KEY_STEP_PX, 0); return; }
    if (e.key === "ArrowRight") { e.preventDefault(); renderer?.panByClientPixels( PAN_KEY_STEP_PX, 0); return; }

    // Unit pad — QWER=row0, ASDF=row1, ZXCV=row2. Rows are bound to fixed
    // hotkeys; the *tier* sitting on each row slides as the player progresses
    // (see visibleUnitTiers). Read the unit type from the rendered button so
    // fork-slot reskins (Footman→Elder, Raider→Juggernaut, Lancer→SkyLancer)
    // are honoured.
    const visibleHotkeys = visibleUnitTierHotkeys();
    for (let row = 0; row < visibleHotkeys.length; row++) {
      const col = visibleHotkeys[row]!.indexOf(k);
      if (col < 0) continue;
      const btn = document.querySelector<HTMLButtonElement>(
        `#unit-pad .unit-slot[data-tier="${row}"][data-col="${col}"]`,
      );
      if (btn === null || btn.disabled) return;
      const unitType = btn.dataset.unitType as UnitType | undefined;
      if (unitType === undefined) return;
      sendUnit(unitType);
      return;
    }
  });
}

function sendUnit(unitType: UnitType): void {
  if (appState.postGame) return; // Frozen battlefield — no more unit sends.
  if (summonModeActive) {
    socket?.send(JSON.stringify({ type: "cheat", code: "summon", unitType }));
    return;
  }
  // "Attack the next player": first alive opponent in the room order after us
  const myIdx = appState.roomPlayerIds.indexOf(appState.sessionId);
  if (myIdx < 0) return;
  const ordered = [
    ...appState.roomPlayerIds.slice(myIdx + 1),
    ...appState.roomPlayerIds.slice(0, myIdx),
  ];
  const alivePlayers = new Set(
    (latestSnapshot?.players ?? [])
      .filter((p) => p.lives > 0)
      .map((p) => p.playerId),
  );
  const targetId = ordered.find((id) => alivePlayers.has(id)) ?? ordered[0];
  if (targetId === undefined) return;
  socket?.send(JSON.stringify({ type: "send_unit", unitType, targetPlayerId: targetId }));
}

// ---------------------------------------------------------------------------
// Snapshot-driven updates — player frames, top HUD, action bar, research
// ---------------------------------------------------------------------------

function setText(id: string, text: string, flash = false, title?: string): void {
  const el = document.querySelector<HTMLElement>(`#${id}`);
  if (el === null) return;
  if (title !== undefined && el.title !== title) el.title = title;
  if (el.textContent === text) return;
  el.textContent = text;
  if (flash) {
    el.classList.remove("flash");
    void el.offsetWidth;
    el.classList.add("flash");
    setTimeout(() => el.classList.remove("flash"), 400);
  }
}

function updatePlayerFrames(snapshot: SnapshotMessage): void {
  for (let i = 0; i < appState.roomPlayerIds.length; i++) {
    const playerId = appState.roomPlayerIds[i]!;
    const player   = snapshot.players.find((p) => p.playerId === playerId);
    if (player === undefined) continue;

    const frame = document.querySelector<HTMLElement>(`#pframe-${i}`);
    if (frame === null) continue;

    const dead = player.lives <= 0;
    const left = player.hasLeft === true;
    frame.classList.toggle("dead", dead);
    frame.classList.toggle("has-left", left && !dead);
    frame.classList.toggle("danger", !dead && !left && player.lives <= 5);

    const leftEl = document.querySelector<HTMLElement>(`#pf-left-${i}`);
    if (leftEl !== null) leftEl.hidden = !left || dead;
    const deadEl = document.querySelector<HTMLElement>(`#pf-dead-${i}`);
    if (deadEl !== null) deadEl.hidden = !dead;

    const prev = prevStats.get(playerId);
    setText(`pf-lives-${i}`, String(player.lives), prev?.lives !== player.lives);
    setText(`pf-income-${i}`, `+${fmtCompact(player.income)}`, false, `+${fmtThousands(player.income)}`);

    const factionsEl = document.querySelector<HTMLElement>(`#pf-factions-${i}`);
    if (factionsEl !== null) {
      for (const slot of factionsEl.querySelectorAll<HTMLElement>(".pframe-faction")) {
        const tier = slot.dataset.tier as "3" | "4" | "5" | undefined;
        if (tier === undefined) continue;
        const picked = player.factionPicks[`t${tier}` as "t3" | "t4" | "t5"];
        slot.dataset.faction = picked ?? "";
      }
    }

    prevStats.set(playerId, { lives: player.lives, gold: player.gold, techPoints: player.techPoints });
  }
}

/** Format a number with thin-space thousands separators — matches wiki rendering. */
function fmtThousands(n: number): string {
  return n.toLocaleString("en-US").replace(/,/g, " ");
}

/**
 * Compact HUD format. Below 10 000 → full digits with thin-space separators.
 * Above → 3-significant-figure K/M/B/T (e.g. 12.5K, 142K, 1.23M, 12.3M, 123M).
 * Width capped at 5 chars (sign aside) so HUD cells don't grow with the
 * economy. Pair with a `title` attribute carrying the exact `fmtThousands`
 * value when precision matters on hover.
 *
 * Truncates (floors) at every precision so the displayed value is a strict
 * lower bound on the real value — 999 999 g shows "999K" not "1.00M", so a
 * player never thinks they can afford a 1M-cost unit when they actually have
 * less. The unit jump happens exactly at the round number (1 000 000 → 1.00M).
 */
function fmtCompact(n: number): string {
  if (n < 10_000) return fmtThousands(n);
  const units: ReadonlyArray<readonly [number, string]> = [
    [1_000, "K"],
    [1_000_000, "M"],
    [1_000_000_000, "B"],
    [1_000_000_000_000, "T"],
  ];
  for (let i = 0; i < units.length; i++) {
    const [divisor, suffix] = units[i]!;
    const hasNext = i + 1 < units.length;
    const value = n / divisor;
    // Escalate only when this unit literally can't represent the value (>= 1000).
    if (hasNext && value >= 1000) continue;
    if (value < 10)  return `${(Math.floor(value * 100) / 100).toFixed(2)}${suffix}`; // 1.00–9.99
    if (value < 100) return `${(Math.floor(value * 10) / 10).toFixed(1)}${suffix}`;   // 10.0–99.9
    return `${Math.floor(value)}${suffix}`;                                            // 100–999
  }
  return n.toExponential(2); // unreachable for any sane in-game value
}

function updateTopHud(snapshot: SnapshotMessage): void {
  const me = snapshot.players.find((p) => p.playerId === appState.sessionId);
  if (me === undefined) return;

  const prev = prevStats.get(appState.sessionId);
  setText("thud-gold",   fmtCompact(me.gold),         prev?.gold !== me.gold,  fmtThousands(me.gold));
  setText("thud-tp",     String(me.techPoints),       prev?.techPoints !== me.techPoints);
  setText("thud-income", `+${fmtCompact(me.income)}`, false,                   `+${fmtThousands(me.income)}`);

  const dead = me.lives <= 0;
  setText("thud-lives", dead ? "DEAD" : String(me.lives), prev?.lives !== me.lives);
  const livesWrap = document.getElementById("thud-lives-wrap");
  if (livesWrap !== null) {
    livesWrap.classList.toggle("danger", !dead && me.lives <= 5);
    livesWrap.classList.toggle("dead", dead);
  }

  const ticksUntil = INCOME_INTERVAL_TICKS - (snapshot.tick % INCOME_INTERVAL_TICKS);
  const seconds    = Math.ceil(ticksUntil * 0.1);
  const justPaid   = ticksUntil === INCOME_INTERVAL_TICKS;
  setText("thud-timer", `${seconds}`, justPaid);

  // Ring drains from full → empty over each 10s income cycle. progress=1 at
  // the moment income just paid (full ring), progress→0 just before next pay.
  // When the offset drops (cycle wraps), snap instantly with no transition so
  // the ring doesn't visibly grow back from empty to full.
  const ringFill = document.getElementById("income-ring-fill") as SVGCircleElement | null;
  if (ringFill !== null) {
    const progress = ticksUntil / INCOME_INTERVAL_TICKS;
    const newOffset = INCOME_RING_CIRC * (1 - progress);
    if (newOffset < prevIncomeRingOffset - 1) {
      ringFill.classList.add("snap");
      ringFill.style.strokeDashoffset = String(newOffset);
      requestAnimationFrame(() => ringFill.classList.remove("snap"));
    } else {
      ringFill.style.strokeDashoffset = String(newOffset);
    }
    prevIncomeRingOffset = newOffset;
  }
}

/**
 * Re-skin the T1 / T2 fork slots to reflect which fork option the player has picked,
 * and toggle the Lancer ↔ Sky Lancer reskin when t2_b1_airmount is researched.
 * Slot defaults: T1 col 3 → Elder, T2 col 3 → Juggernaut. The lancer col reskins to
 * Sky Lancer when airmount is owned (server treats them as separate units, but the
 * 4-col unit pad shows only one — see TIER2 comment).
 */
function reskinForkSlots(techOwned: string[]): void {
  type ForkSwap = { tierNum: number; col: number; preferred: UnitType; gate: string };
  const swaps: ForkSwap[] = [];
  if (techOwned.includes("t1_b2_footmen"))   swaps.push({ tierNum: 1, col: 3, preferred: "footmen", gate: "t1_b2_footmen" });
  if (techOwned.includes("t2_b2_raider"))    swaps.push({ tierNum: 2, col: 3, preferred: "raider",  gate: "t2_b2_raider" });

  let needsHydrate = swaps.length > 0;
  for (const s of swaps) {
    const btn = document.querySelector<HTMLButtonElement>(`#unit-pad .unit-slot[data-tier-num="${s.tierNum}"][data-col="${s.col}"]`);
    if (btn === null) continue;
    if (btn.dataset.unitType === s.preferred) continue; // already swapped
    btn.dataset.unitType = s.preferred;
    btn.dataset.gateTech = s.gate;
    const icon = btn.querySelector<HTMLElement>(".icon-slot");
    if (icon !== null) {
      icon.dataset.icon = s.preferred;
      // Clear the rendered canvas so hydrateIcons re-renders with the new silhouette.
      icon.innerHTML = "";
    }
    const costEl = btn.querySelector<HTMLElement>(".slot-cost");
    if (costEl !== null) {
      const cost = UNIT_COSTS[s.preferred];
      costEl.textContent = fmtCompact(cost);
      costEl.title = `${cost.toLocaleString("en-US")} gold`;
    }
  }

  // Lancer → Sky Lancer reskin. Substitution research: post-airmount, the
  // server rejects send_unit("lancer") (see canSendUnit), so the UI swaps the
  // Lancer slot to a Sky Lancer button. Click sends `skyLancer` directly —
  // the button's `data-unit-type` swaps to keep stockpile/affordability/cost
  // consistent.
  const reskins: { selector: string; gate: string; from: UnitType; to: UnitType; activeClass: string }[] = [
    { selector: `#unit-pad .unit-slot[data-tier-num="2"][data-col="2"]`, gate: "t2_b1_airmount", from: "lancer", to: "skyLancer", activeClass: "airmount-active" },
  ];
  for (const r of reskins) {
    const btn = document.querySelector<HTMLButtonElement>(r.selector);
    if (btn === null) continue;
    const wantUpgrade = techOwned.includes(r.gate);
    const showingUpgrade = btn.classList.contains(r.activeClass);
    if (wantUpgrade !== showingUpgrade) {
      btn.classList.toggle(r.activeClass, wantUpgrade);
      btn.dataset.unitType = wantUpgrade ? r.to : r.from;
      const icon = btn.querySelector<HTMLElement>(".icon-slot");
      if (icon !== null) {
        icon.dataset.icon = wantUpgrade ? r.to : r.from;
        icon.innerHTML = "";
        needsHydrate = true;
      }
      const costEl = btn.querySelector<HTMLElement>(".slot-cost");
      if (costEl !== null) {
        const cost = UNIT_COSTS[wantUpgrade ? r.to : r.from];
        costEl.textContent = fmtCompact(cost);
        costEl.title = `${cost.toLocaleString("en-US")} gold`;
      }
    }
  }

  if (needsHydrate) hydrateIcons(document.getElementById("unit-pad") ?? document);
}

function updateActionBar(snapshot: SnapshotMessage): void {
  const me = snapshot.players.find((p) => p.playerId === appState.sessionId);
  if (me === undefined) return;

  const gold = me.gold;
  const techOwned = me.techOwned;

  reskinForkSlots(techOwned);

  const currentTick = snapshot.tick;

  document.querySelectorAll<HTMLButtonElement>("#unit-pad .unit-slot").forEach((btn) => {
    const type = btn.dataset.unitType as UnitType | "";
    if (!type) return;
    const cost = UNIT_COSTS[type];
    const gate = btn.dataset.gateTech ?? "";
    const techUnlocked = gate === "" || techOwned.includes(gate);
    const entry = me.unitStockpile[type];
    const delayTicks = UNIT_UNLOCK_DELAY_TICKS[type];
    const stockEl = btn.querySelector<HTMLElement>(".slot-stock");
    const delayEl = btn.querySelector<HTMLElement>(".slot-delay");

    let inDelay = false;
    let delayRemainingS = 0;
    if (techUnlocked && entry.unlockTick !== null) {
      const delayEnd = entry.unlockTick + delayTicks;
      if (currentTick < delayEnd) {
        inDelay = true;
        delayRemainingS = Math.ceil((delayEnd - currentTick) * 0.1);
      }
    }

    const sendable = techUnlocked && !inDelay && entry.stockpile > 0;
    const unaffordable = sendable && gold < cost;

    btn.classList.toggle("locked", !techUnlocked);
    btn.classList.toggle("stockpile-delay", techUnlocked && inDelay);
    btn.classList.toggle("unaffordable", unaffordable);
    btn.disabled = summonModeActive ? false : (!techUnlocked || inDelay || entry.stockpile <= 0);

    if (stockEl !== null) {
      stockEl.textContent = techUnlocked && !inDelay
        ? `${entry.stockpile}/${STOCKPILE_CAP[type]}`
        : "";
    }
    if (delayEl !== null) {
      delayEl.textContent = inDelay ? `${delayRemainingS}s` : "";
    }
  });
}

/**
 * Reflect a player's faction pick at this tier into the DOM. When the pick is
 * `null`, shows the multichoice card and locks the 3 power slots. When set,
 * collapses the multichoice into the "Path: <Faction>" banner, populates the
 * 3 power slots from FACTIONS[id].powers (currently placeholders), and applies
 * the faction's color tint to the row.
 */
const FACTION_MODIFIER_CLASSES = FACTION_IDS.map((id) => `rtier--faction-${id}`);

/** Populate the 3 faction-power slots for a tier. Called only when the section
 *  is entering the PICKED state — the slots stay empty (and hidden by the tier
 *  state class) until then. The tier state classes (.is-locked/.is-unlocked/
 *  .is-picked) and the faction modifier are set by updateResearchPanel. */
function fillFactionPowerSlots(tier: 3 | 4 | 5, factionId: FactionId): void {
  const section = document.querySelector<HTMLElement>(`#rtier-${tier}`);
  if (section === null) return;
  const row = section.querySelector<HTMLElement>(".rcards-row--faction");
  if (row === null) return;
  const faction = FACTIONS[factionId];
  const slots = row.querySelectorAll<HTMLElement>(".rcard--faction-power");
  faction.powers.forEach((power, idx) => {
    const slot = slots[idx];
    if (slot === undefined) return;
    slot.classList.remove("rcard--locked");
    slot.classList.add("rcard--unlocked");
    slot.innerHTML = `
      ${cardBackHtml(tier)}
      <div class="rcard-header"><span class="rnode-name rcard-name">${escapeHtml(power.name)}</span></div>
      <div class="rcard-art">
        <span class="rcard-faction-power-placeholder" aria-hidden="true">…</span>
      </div>
      <div class="rcard-body">
        <p class="rcard-desc">${escapeHtml(power.description.length > 0 ? power.description : "Coming soon.")}</p>
      </div>
    `;
  });
}

/** Reset the 3 faction-power slots back to empty card-backs. Called when the
 *  tier leaves PICKED (rejoin / baseline reset edge cases). */
function clearFactionPowerSlots(tier: 3 | 4 | 5): void {
  const section = document.querySelector<HTMLElement>(`#rtier-${tier}`);
  if (section === null) return;
  const row = section.querySelector<HTMLElement>(".rcards-row--faction");
  if (row === null) return;
  row.querySelectorAll<HTMLElement>(".rcard--faction-power").forEach((slot, idx) => {
    slot.classList.remove("rcard--unlocked");
    slot.innerHTML = cardBackHtml(tier);
    slot.dataset.slotIdx = String(idx);
  });
}

/** Drive the .is-locked / .is-unlocked / .is-picked + faction-modifier classes
 *  on a single tier section. Also fills the faction-power slots and updates the
 *  header faction-name when entering PICKED. */
function applyTierState(tier: number, unlocked: boolean, factionId: FactionId | null): void {
  const section = document.querySelector<HTMLElement>(`#rtier-${tier}`);
  if (section === null) return;
  section.classList.remove("is-locked", "is-unlocked", "is-picked");
  section.classList.remove(...FACTION_MODIFIER_CLASSES);

  const factionNameEl = section.querySelector<HTMLElement>(".rtier-faction-name");

  if (!unlocked) {
    section.classList.add("is-locked");
    if (factionNameEl !== null) { factionNameEl.textContent = ""; factionNameEl.hidden = true; }
    if (tier >= 3) clearFactionPowerSlots(tier as 3 | 4 | 5);
    return;
  }

  // Unlocked. T1/T2 skip the multichoice step — they're factionless and go
  // straight to PICKED (showing their regular 3 slots).
  if (tier <= 2 || factionId !== null) {
    section.classList.add("is-picked");
    if (tier >= 3 && factionId !== null) {
      section.classList.add(`rtier--faction-${factionId}`);
      fillFactionPowerSlots(tier as 3 | 4 | 5, factionId);
      if (factionNameEl !== null) {
        factionNameEl.textContent = FACTIONS[factionId].name;
        factionNameEl.hidden = false;
      }
    } else if (factionNameEl !== null) {
      factionNameEl.textContent = "";
      factionNameEl.hidden = true;
    }
    return;
  }

  // Unlocked but no faction picked yet (T3/T4/T5).
  section.classList.add("is-unlocked");
  if (factionNameEl !== null) { factionNameEl.textContent = ""; factionNameEl.hidden = true; }
  clearFactionPowerSlots(tier as 3 | 4 | 5);
  // Reset the path panels to clickable (in case of rejoin from a stale state
  // where buttons were disabled by a previous PICKED phase).
  section.querySelectorAll<HTMLButtonElement>(".rfaction-option").forEach((btn) => {
    btn.hidden = false;
    btn.disabled = false;
  });
}

function nodeIsOwned(node: ResearchNode, techOwned: string[]): boolean {
  // Repeatable nodes (Lane Expansion) are never "owned" — each click is another purchase.
  if (node.repeatable === true) return false;
  if (node.choices !== undefined) return node.choices.some((c) => techOwned.includes(c.id));
  return techOwned.includes(node.id);
}

function updateResearchPanel(snapshot: SnapshotMessage): void {
  const me = snapshot.players.find((p) => p.playerId === appState.sessionId);
  if (me === undefined) return;
  const gold = me.gold;
  const techPoints = me.techPoints;
  const techOwned = me.techOwned;
  const laneExpansionLevel = me.laneExpansionLevel;

  // Drive each tier's three-state visual: LOCKED / UNLOCKED (faction tiers
  // only) / PICKED. The state class swaps which body children show — circles
  // (UNLOCKED) vs the 3 cards (PICKED) — and triggers the bg-image swap when
  // a faction is committed.
  for (let tier = 1; tier <= 5; tier++) {
    const spineId = TIER_SPINE_NODE_ID[tier]!;
    const unlocked = techOwned.includes(spineId);
    const factionId = tier >= 3 ? me.factionPicks[(`t${tier}`) as "t3" | "t4" | "t5"] : null;
    applyTierState(tier, unlocked, factionId);
  }

  let anyPurchasable = false;
  for (const node of RESEARCH_TREE) {
    const btn = document.querySelector<HTMLElement>(`#rnode-${node.id}`);
    const bought = nodeIsOwned(node, techOwned);
    const prereqsMet = (node.prereqs ?? []).every((p) => techOwned.includes(p));
    const affordable = gold >= node.cost && (node.tpCost === undefined || techPoints >= node.tpCost);

    if (!bought && prereqsMet && affordable) anyPurchasable = true;

    if (btn === null) continue;
    btn.classList.toggle("done", bought);
    btn.classList.toggle("locked", !bought && !prereqsMet);
    btn.classList.toggle("unaffordable", !bought && prereqsMet && !affordable);
    // Unbought multichoice cards are <div>s — propagate disabled state to
    // their inner half-tile <button>s. Everything else is a real <button>.
    if (node.choices !== undefined && !bought) {
      btn.querySelectorAll<HTMLButtonElement>(".rcard-half").forEach((half) => {
        half.disabled = !prereqsMet || !affordable;
      });
    } else if (btn instanceof HTMLButtonElement) {
      btn.disabled = bought || !prereqsMet || !affordable;
    }

    if (node.choices !== undefined && bought) {
      const chosen = node.choices.find((c) => techOwned.includes(c.id));
      if (chosen !== undefined && !btn.classList.contains("rcard--resolved")) {
        btn.classList.add("rnode--resolved", "rcard--resolved");
        btn.classList.remove("rcard--choice");
        // Rebuild the resolved card so it matches a regular single-unit card:
        // chosen unit's silhouette in the art well, full body, green stamp on
        // top, and the locked-tier back-of-card watermark in case the player
        // somehow views this before unlocking (shouldn't, but kept consistent).
        const tier = researchRowTier(node.row);
        btn.innerHTML = `
          ${cardBackHtml(tier)}
          ${renderCardHeaderHtml(chosen.name)}
          <div class="rcard-art">
            <span class="rcard-icon icon-slot"
                  data-icon="${escapeHtml(chosen.icon)}"
                  data-icon-size="56"></span>
          </div>
          ${cardBodyHtml(chosen.id)}
        `;
        hydrateIcons(btn);
      }
    }

    // Repeatable badge: show "+N" in the bottom-right corner once at least one
    // level has been purchased. Sits in the same spot the .done tick would
    // occupy on a one-shot card.
    if (node.repeatable === true && node.id === "t3_b1_lane_expansion") {
      const lv = laneExpansionLevel;
      const badge = btn.querySelector<HTMLElement>(".rnode-level");
      if (badge !== null) {
        badge.textContent = lv > 0 ? `+${lv}` : "";
        badge.dataset.level = String(lv);
      }
    }
  }

  document.querySelector<HTMLButtonElement>("#research-btn")
    ?.classList.toggle("has-purchasable", anyPurchasable);

  const ptsEl = document.getElementById("research-points");
  if (ptsEl !== null) {
    const valEl = ptsEl.querySelector<HTMLElement>(".research-points-val");
    const labEl = ptsEl.querySelector<HTMLElement>(".research-points-label");
    if (valEl !== null) valEl.textContent = String(techPoints);
    if (labEl !== null) labEl.textContent = techPoints === 1 ? "Tech Pt" : "Tech Pts";
    ptsEl.classList.toggle("no-points", techPoints === 0);
    ptsEl.classList.toggle("has-points", techPoints > 0);
  }
}

// ---------------------------------------------------------------------------
// WebSocket message handlers
// ---------------------------------------------------------------------------

function handleQueueState(msg: QueueStateMessage): void {
  appState.queuedPlayers   = msg.queuedPlayers;
  appState.countdownEndsAt = msg.countdownEndsAt;
  // Surgically refresh the landing queue UI so the countdown ring rAF tick
  // doesn't get interrupted and any in-flight name edit survives.
  if (appState.view === "landing" && appState.activeRoomId === null) {
    updateLandingQueueUI();
  }
}

function handleMatchFound(msg: MatchFoundMessage): void {
  appState.status = `Match found: ${msg.roomId}`;
  appState.inQueue = false;
}

function handleMatchStart(msg: MatchStartMessage): void {
  appState.activeRoomId    = msg.roomId;
  appState.roomPlayerIds   = msg.playerIds;
  appState.roomPlayerNames = { ...msg.playerNames };
  appState.view            = "match";
  appState.lobbyState      = null;
  // In-match cap is canonical: a server reboot between landing and match could
  // have changed TH_MAX_TIER, so the match's value wins over /api/stats.
  if (Number.isInteger(msg.maxTier) && msg.maxTier >= 2 && msg.maxTier <= 5) {
    appState.maxTier = msg.maxTier;
  }
  if (Array.isArray(msg.enabledFactions)) {
    appState.enabledFactions = msg.enabledFactions.filter((id): id is FactionId =>
      FACTION_IDS.includes(id),
    );
  }
  setLocationPath("/");
  latestSnapshot     = null;
  matchStartTime     = Date.now();
  lastRenderedPadTier = -1;
  appState.postGame  = false;
  mountMatchCanvas(msg.playerIds.length);
}

function handleSnapshotDelta(msg: SnapshotDeltaMessage): void {
  // The first message after match_start / reconnect is always a full
  // snapshot; the server's onSocketReplaced hook in ConnectionRegistry
  // guarantees this. A delta arriving before any baseline means desync —
  // log and wait for the next full snapshot rather than corrupt the mirror.
  if (latestSnapshot === null) {
    console.warn("snapshot_delta before baseline; ignoring", msg.tick);
    return;
  }
  const reconstructed = applySnapshotDelta(latestSnapshot, msg);
  handleSnapshot(reconstructed);
}

function handleSnapshot(msg: SnapshotMessage): void {
  debugOverlay.recordSnapshotArrival(msg.tick, performance.now());
  latestSnapshot = msg;
  renderer?.applySnapshot(msg);
  updatePlayerFrames(msg);
  updateTopHud(msg);
  // Re-render the unit pad if the player's tier crossed a threshold this
  // snapshot — the visible 3-row window slides up. updateActionBar's per-tick
  // mutation pass then refreshes stock / delay / disabled / fork reskins.
  const newTier = currentPlayerTier();
  if (newTier !== lastRenderedPadTier) {
    lastRenderedPadTier = newTier;
    const pad = document.getElementById("unit-pad");
    if (pad !== null) {
      pad.innerHTML = renderUnitPadHtml();
      attachUnitPadListeners();
      hydrateIcons(pad);
    }
  }
  updateActionBar(msg);
  updateResearchPanel(msg);
}

function handlePlayerEliminated(msg: PlayerEliminatedMessage): void {
  const idx   = appState.roomPlayerIds.indexOf(msg.playerId);
  const label = idx >= 0 ? displayNameFor(msg.playerId, idx) : msg.playerId;
  showToast(`${label} eliminated`);

  if (msg.playerId !== appState.sessionId) return;

  // 1v1 case: server sends player_eliminated immediately followed by game_over
  // in the same tick. Skip the spectator panel when fewer than 2 players will
  // remain alive — handleGameOver will own the overlay a moment later.
  const aliveCount = (latestSnapshot?.players ?? []).filter((p) => p.lives > 0).length;
  if (aliveCount < 2) return;

  showSelfDefeatOverlay({ final: false });
}

/**
 * Renders the Defeat / Victory overlay over the live (or frozen) battlefield.
 * `final=false` is the spectator-mode panel shown on self-elimination while
 * the match keeps running — the renderer stays alive, no `postGame` flip,
 * dismissing returns the user to a still-live game. `final=true` is the
 * game-over panel; the renderer is already frozen by handleGameOver before
 * this is called.
 */
function showSelfDefeatOverlay(opts: { final: boolean; isWinner?: boolean }): void {
  const elapsed = matchStartTime !== null ? Math.round((Date.now() - matchStartTime) / 1000) : null;
  const isWinner = opts.isWinner === true;
  const heading = isWinner ? "Victory" : "Defeat";

  // Strip any prior overlay first — covers the player_eliminated → game_over
  // succession (spectator panel replaced by the final one) and any defensive
  // double-fire.
  document.getElementById("game-over-overlay")?.remove();

  const matchRoot = document.getElementById("match-root");
  const overlay = document.createElement("div");
  overlay.id = "game-over-overlay";
  overlay.className = `game-over-overlay ${isWinner ? "game-over-win" : "game-over-loss"}`;
  overlay.innerHTML = `
    <div class="game-over-backdrop" id="game-over-backdrop"></div>
    <div class="game-over-window">
      <button class="game-over-close" id="game-over-close" aria-label="Close">✕</button>
      <h1 class="game-over-heading">${heading}</h1>
      ${!opts.final ? `<p class="game-over-subtext">You're out — others are still battling. Close this to spectate.</p>` : ""}
      ${elapsed !== null ? `<p class="game-over-duration">${formatDuration(elapsed)}</p>` : ""}
      <a class="game-over-discord chrome-discord"
         href="${DISCORD_URL}" target="_blank" rel="noopener noreferrer">
        ${DISCORD_LOGO_SVG}
        <strong class="game-over-discord-headline">Tell us how it went.</strong>
        <span class="game-over-discord-sub">Bugs, balance gripes, GGs — all welcome in our Discord.</span>
      </a>
      <div class="lobby-actions" style="margin-top:1.5rem">
        <button class="lobby-btn" id="btn-back-to-menu">Back to Menu</button>
      </div>
    </div>
  `;
  (matchRoot ?? appElement).appendChild(overlay);

  document.getElementById("game-over-close")?.addEventListener("click", () => {
    document.getElementById("game-over-overlay")?.remove();
  });
  document.getElementById("btn-back-to-menu")?.addEventListener("click", leaveMatch);
}

function handlePlayerLeft(msg: PlayerLeftMessage): void {
  const idx   = appState.roomPlayerIds.indexOf(msg.playerId);
  const label = idx >= 0 ? displayNameFor(msg.playerId, idx) : msg.playerId;
  showToast(`${label} left the game`);
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function handleGameOver(msg: GameOverMessage): void {
  const isWinner = msg.winnerPlayerId === appState.sessionId;

  // Freeze the simulation: stop the renderer's RAF so the canvas keeps the last
  // drawn frame. Don't clear latestSnapshot / activeRoomId / roomPlayerIds /
  // prevStats — the HUD reads them and we want it frozen at final values too.
  renderer?.destroy();
  renderer = null;
  resizeObserver?.disconnect();
  resizeObserver = null;
  appState.postGame = true;

  // Close any open in-match modals so the overlay isn't sandwiched.
  if (researchExpanded) setResearchOpen(false);
  if (settingsExpanded) setSettingsOpen(false);

  document.getElementById("match-root")?.classList.add("match-ended");

  showSelfDefeatOverlay({ final: true, isWinner });

  // First-match welcome nudge — fires once ever, then localStorage-guarded.
  if (window.localStorage.getItem(FIRST_MATCH_TOAST_KEY) === null) {
    window.localStorage.setItem(FIRST_MATCH_TOAST_KEY, "1");
    showToast("👋 First match done — devs + other players hang out on Discord.", 6000);
  }
}

function showToast(text: string, durationMs = 3000): void {
  document.querySelector(".match-overlay")?.remove();
  const el = document.createElement("div");
  el.className   = "match-overlay";
  el.textContent = text;
  appElement.appendChild(el);
  setTimeout(() => el.remove(), durationMs);
}

/**
 * Server-initiated forced match end. Today only fires when the worker shard
 * hosting this match crashes (Phase 3.1b supervisor) — clients in those
 * matches need to leave the now-defunct room and return to the landing
 * screen. The WS socket itself stays open; the player can re-queue or start
 * a custom match immediately. Critically: the WS close handler must NOT
 * treat this as a reconnect trigger (the socket isn't closing, just the
 * room is gone).
 */
function handleMatchAborted(msg: MatchAbortedMessage): void {
  // If this aborted msg is for a room we're no longer in, ignore (rare race
  // — game_over could have arrived first via a different path, or the
  // player already navigated away).
  if (appState.activeRoomId !== null && appState.activeRoomId !== msg.roomId) return;

  renderer?.destroy();
  renderer = null;
  resizeObserver?.disconnect();
  resizeObserver = null;
  matchStartTime = null;
  latestSnapshot = null;

  appState.activeRoomId    = null;
  appState.roomPlayerIds   = [];
  appState.roomPlayerNames = {};
  appState.view            = "landing";
  appState.postGame        = false;
  prevStats.clear();
  renderActiveScreen();

  const reasonText = msg.reason === "server_error"
    ? "The match server hiccupped. Match ended; you can queue again."
    : "Match aborted by the server.";
  showToast(reasonText);
}

function handleCustomLobbyState(msg: CustomLobbyStateMessage): void {
  appState.lobbyState = {
    lobbyId: msg.lobbyId,
    hostId: msg.hostId,
    players: msg.players,
    maxPlayers: msg.maxPlayers,
  };
  appState.view = "custom_lobby";
  appState.status = "In lobby";
  setLocationPath(`/r/${msg.lobbyId}`);
  renderActiveScreen();
}

function handleCustomLobbyError(msg: CustomLobbyErrorMessage): void {
  const messages: Record<string, string> = {
    not_found:       "Lobby not found.",
    already_started: "That game has already started.",
    full:            "That lobby is full.",
    closed:          "The lobby was closed.",
    not_host:        "Only the host can do that.",
    too_few_players: "Need at least 2 players to start.",
  };
  // too_few_players and not_host don't warrant a view change; stay in lobby.
  if (msg.code === "too_few_players" || msg.code === "not_host") {
    appState.status = messages[msg.code]!;
    renderActiveScreen();
    return;
  }
  appState.lobbyState = null;
  appState.view = "landing";
  appState.status = messages[msg.code] ?? "Something went wrong.";
  setLocationPath("/");
  renderActiveScreen();
}

function handleCapacityState(msg: CapacityStateMessage): void {
  const prev = appState.capacity;
  appState.capacity = { active: msg.active, cap: msg.cap, atCapacity: msg.atCapacity };
  // Re-render landing if the at-capacity flag flipped, so banner/buttons update.
  const flipped = prev?.atCapacity !== msg.atCapacity;
  if (flipped && appState.view === "landing" && appState.activeRoomId === null) {
    renderActiveScreen();
  }
}

function handleServerMessage(msg: ServerMessage): void {
  switch (msg.type) {
    case "queue_state":         handleQueueState(msg);       break;
    case "match_found":         handleMatchFound(msg);       break;
    case "match_start":         handleMatchStart(msg);       break;
    case "snapshot":            handleSnapshot(msg);         break;
    case "snapshot_delta":      handleSnapshotDelta(msg);    break;
    case "player_eliminated":   handlePlayerEliminated(msg); break;
    case "player_left":         handlePlayerLeft(msg);       break;
    case "game_over":           handleGameOver(msg);         break;
    case "match_aborted":       handleMatchAborted(msg);     break;
    case "custom_lobby_state":  handleCustomLobbyState(msg); break;
    case "custom_lobby_error":  handleCustomLobbyError(msg); break;
    case "capacity_state":      handleCapacityState(msg);    break;
    case "event":
      appState.status = msg.message; renderActiveScreen(); break;
    case "error":
      if (msg.code === "server_full") {
        showToast("Towerhold just hit capacity — the page will unlock as soon as a slot frees.");
        // Make sure landing reflects at-capacity immediately, even before the
        // next capacity_state broadcast lands.
        if (appState.capacity !== null) {
          appState.capacity = { ...appState.capacity, atCapacity: true };
        } else {
          appState.capacity = { active: 0, cap: null, atCapacity: true };
        }
        // Reset optimistic queue state so the Quick Play button re-enables paths.
        if (appState.inQueue) appState.inQueue = false;
        appState.view = "landing";
        appState.lobbyState = null;
        setLocationPath("/");
        renderActiveScreen();
        return;
      }
      appState.status = `Error: ${msg.message}`; renderActiveScreen(); break;
  }
}

// ---------------------------------------------------------------------------
// Session & connection bootstrap
// ---------------------------------------------------------------------------

function sendHello(): void {
  if (socket === null || socket.readyState !== WebSocket.OPEN) return;
  socket.send(JSON.stringify({ type: "hello", name: appState.playerName }));
}

function setLocationPath(path: string): void {
  if (window.location.pathname === path) return;
  window.history.replaceState(null, "", path);
}

/** Parse /r/<lobbyId> off the current URL; returns null for anything else. */
function parseLobbyIdFromPath(): string | null {
  const match = /^\/r\/([a-z0-9]{4,16})\/?$/i.exec(window.location.pathname);
  return match?.[1] ?? null;
}

async function getSessionId(): Promise<string> {
  const existing = identityStorage.getItem(SESSION_STORAGE_KEY);
  if (existing !== null) return existing;
  const response = await fetch("/api/session", { method: "POST" });
  if (!response.ok) throw new Error("Failed to create session");
  const data = (await response.json()) as SessionResponse;
  identityStorage.setItem(SESSION_STORAGE_KEY, data.sessionId);
  return data.sessionId;
}

/**
 * Fetch boot-time server info from /api/stats — tier cap (drives wiki sidebar /
 * unit pad filtering) and build version (shown in the footer so bug reports
 * name the build). Network/parse failures leave both at their defaults.
 */
async function loadServerInfo(): Promise<void> {
  try {
    const res = await fetch("/api/stats");
    if (!res.ok) return;
    const data = (await res.json()) as { maxTier?: number; version?: string; env?: string; enabledFactions?: unknown };
    if (typeof data.maxTier === "number" && Number.isInteger(data.maxTier) && data.maxTier >= 2 && data.maxTier <= 5) {
      appState.maxTier = data.maxTier;
    }
    if (typeof data.version === "string" && data.version !== "") {
      appState.serverVersion = data.version;
    }
    if (typeof data.env === "string") {
      debugOverlay.setEnvLabel(data.env);
    }
    if (Array.isArray(data.enabledFactions)) {
      appState.enabledFactions = data.enabledFactions.filter((id): id is FactionId =>
        FACTION_IDS.includes(id as FactionId),
      );
    }
  } catch {
    /* keep defaults */
  }
}

let reconnectAttempts = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

function connect(sessionId: string): void {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  socket = new WebSocket(`${proto}//${window.location.host}/ws?sessionId=${sessionId}`);
  // Snapshot/delta messages arrive as binary frames; we want sync access via
  // ArrayBuffer rather than the default Blob (which would force an async read).
  socket.binaryType = "arraybuffer";

  socket.addEventListener("open", () => {
    appState.connected = true;
    appState.status = "Connected";
    reconnectAttempts = 0;
    // Always announce our current name first, so subsequent state (queue, lobby,
    // match_start) carries the latest.
    sendHello();
    // Fulfill a pending /r/<id> deep link once we're connected.
    if (appState.pendingJoinLobbyId !== null) {
      const targetId = appState.pendingJoinLobbyId;
      appState.pendingJoinLobbyId = null;
      socket?.send(JSON.stringify({ type: "custom_join", lobbyId: targetId }));
    }
    if (appState.activeRoomId === null) renderActiveScreen();
  });

  socket.addEventListener("close", (e) => {
    appState.connected = false;
    // 4000 = missing session (don't retry), 4001 = replaced by new connection (don't retry)
    if (e.code === 4000 || e.code === 4001) {
      appState.status = "Disconnected";
      renderActiveScreen();
      return;
    }
    scheduleReconnect(sessionId);
  });

  socket.addEventListener("message", (e) => {
    if (typeof e.data === "string") {
      handleServerMessage(JSON.parse(e.data) as ServerMessage);
      return;
    }
    if (!(e.data instanceof ArrayBuffer)) {
      console.warn("unexpected binary frame type:", typeof e.data);
      return;
    }
    try {
      const msg = decodeSnapshot(new Uint8Array(e.data));
      handleServerMessage(msg);
    } catch (err) {
      // Codec mismatch (likely server deployed a new schema mid-session).
      // Force a refresh so the client picks up the new build.
      console.error("snapshot decode failed:", err);
      showToast("Server updated — please refresh.");
      socket?.close();
    }
  });
}

function scheduleReconnect(sessionId: string): void {
  const delay = Math.min(1000 * 2 ** reconnectAttempts, 16000);
  reconnectAttempts++;
  appState.status = `Reconnecting… (${reconnectAttempts})`;
  if (appState.activeRoomId === null) renderActiveScreen();
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect(sessionId);
  }, delay);
}

async function bootstrap(): Promise<void> {
  // Debug overlay: off by default everywhere; opt in with ?debug=1 or
  // Ctrl+Shift+D. See apps/client/src/debugOverlay.ts.
  debugOverlay.init();

  // Parallel: session create + tier-cap fetch. Both are server round-trips and
  // independent. We need maxTier before first render so the wiki sidebar
  // doesn't flash unfiltered pages.
  const [sid] = await Promise.all([getSessionId(), loadServerInfo()]);
  appState.sessionId  = sid;
  appState.playerName = loadPlayerName();

  const lobbyFromUrl = parseLobbyIdFromPath();
  if (lobbyFromUrl !== null) {
    appState.pendingJoinLobbyId = lobbyFromUrl;
    appState.status = "Joining lobby…";
  } else if (window.location.pathname === "/how-to-play") {
    appState.view = "docs";
    appState.docsSlug = DEFAULT_DOCS_SLUG;
  } else if (window.location.pathname === "/wiki" || window.location.pathname.startsWith("/wiki/")) {
    appState.view = "docs";
    const slug = window.location.pathname.slice("/wiki/".length).replace(/\/+$/, "");
    appState.docsSlug = slug.length > 0 ? slug : DEFAULT_DOCS_SLUG;
  } else if (window.location.pathname === "/docs" || window.location.pathname.startsWith("/docs/")) {
    appState.view = "docs";
    const slug = window.location.pathname.slice("/docs/".length).replace(/\/+$/, "");
    appState.docsSlug = slug.length > 0 ? slug : DEFAULT_DOCS_SLUG;
  }

  renderActiveScreen();
  connect(appState.sessionId);
}

void bootstrap();

export {}; // keep reconnectTimer referenced
void reconnectTimer;
